{
  "version": "0.1.38",
  "packageName": "@classic-homes/maps-core",
  "description": "Core utilities, types, and themes for Classic Theme MapLibre GL components",
  "subpackages": {
    "main": {
      "importPath": "@classic-homes/maps-core",
      "description": "Main entry point with all exports (themes, types, utilities)",
      "modules": {
        "theme": {
          "description": "MapLibre GL theme generation from design tokens",
          "functions": [
            {
              "name": "generateLightTheme",
              "signature": "() => MapTheme",
              "description": "Generate light theme configuration from design tokens"
            },
            {
              "name": "generateDarkTheme",
              "signature": "() => MapTheme",
              "description": "Generate dark theme configuration from design tokens"
            },
            {
              "name": "lightTheme",
              "signature": "MapTheme",
              "description": "Pre-generated light theme object (singleton)"
            },
            {
              "name": "darkTheme",
              "signature": "MapTheme",
              "description": "Pre-generated dark theme object (singleton)"
            },
            {
              "name": "lightBasicStyle",
              "signature": "StyleSpecification",
              "description": "Pre-generated light MapLibre style using OSM raster tiles"
            },
            {
              "name": "darkBasicStyle",
              "signature": "StyleSpecification",
              "description": "Pre-generated dark MapLibre style using OSM raster tiles"
            },
            {
              "name": "THEME_LIGHT",
              "signature": "'classic-light'",
              "description": "Theme name constant for light theme"
            },
            {
              "name": "THEME_DARK",
              "signature": "'classic-dark'",
              "description": "Theme name constant for dark theme"
            },
            {
              "name": "generateLightColors",
              "signature": "() => MapThemeColors",
              "description": "Generate light theme colors from design tokens"
            },
            {
              "name": "generateDarkColors",
              "signature": "() => MapThemeColors",
              "description": "Generate dark theme colors from design tokens"
            },
            {
              "name": "generateLightDataColors",
              "signature": "() => MapDataColors",
              "description": "Generate light theme data visualization colors"
            },
            {
              "name": "generateDarkDataColors",
              "signature": "() => MapDataColors",
              "description": "Generate dark theme data visualization colors"
            },
            {
              "name": "getTypography",
              "signature": "() => { fontFamily: string; sizes: Record<string, number> }",
              "description": "Get typography settings from design tokens"
            },
            {
              "name": "getTheme",
              "signature": "(mode: 'light' | 'dark') => MapTheme",
              "description": "Get a theme by mode name"
            },
            {
              "name": "generateBasicStyle",
              "signature": "(theme: MapTheme) => StyleSpecification",
              "description": "Generate a basic OSM raster tile style for a theme"
            },
            {
              "name": "generateVectorStyle",
              "signature": "(theme: MapTheme, options?: { tileUrl?: string }) => StyleSpecification",
              "description": "Generate a vector tile style for a theme"
            },
            {
              "name": "getBasicStyle",
              "signature": "(mode: 'light' | 'dark') => StyleSpecification",
              "description": "Get a basic raster style by mode name"
            }
          ]
        },
        "format": {
          "description": "Formatting utilities for map coordinates and measurements",
          "functions": [
            {
              "name": "formatCoordinates",
              "signature": "(coordinates: LngLat, precision?: number, format?: 'decimal' | 'dms' | 'dd') => string",
              "description": "Format coordinates for display (decimal, DMS, or DD format)",
              "example": "formatCoordinates([-122.4194, 37.7749]) // '-122.419400, 37.774900'"
            },
            {
              "name": "decimalToDMS",
              "signature": "(decimal: number, type: 'lat' | 'lng') => string",
              "description": "Convert decimal degrees to DMS (degrees, minutes, seconds) format",
              "example": "decimalToDMS(37.7749, 'lat') // '37° 46' 29.6\" N'"
            },
            {
              "name": "formatDistance",
              "signature": "(meters: number, unit?: 'metric' | 'imperial') => string",
              "description": "Format distance in meters/kilometers or feet/miles",
              "example": "formatDistance(1500) // '1.5 km'"
            },
            {
              "name": "formatArea",
              "signature": "(sqMeters: number, unit?: 'metric' | 'imperial') => string",
              "description": "Format area in square meters/kilometers or acres",
              "example": "formatArea(10000) // '1.00 ha'"
            },
            {
              "name": "formatBounds",
              "signature": "(bounds: MapBounds | BoundsArray) => string",
              "description": "Format map bounds as readable string"
            },
            {
              "name": "formatZoom",
              "signature": "(zoom: number) => string",
              "description": "Format zoom level for display",
              "example": "formatZoom(12.5) // 'Zoom: 12.5'"
            },
            {
              "name": "formatBearing",
              "signature": "(bearing: number) => string",
              "description": "Format bearing/rotation as degrees with cardinal direction",
              "example": "formatBearing(45) // '45° NE'"
            },
            {
              "name": "formatPitch",
              "signature": "(pitch: number) => string",
              "description": "Format pitch/tilt for display",
              "example": "formatPitch(60) // '60° tilt'"
            },
            {
              "name": "formatScale",
              "signature": "(scale: number) => string",
              "description": "Format a scale ratio for display",
              "example": "formatScale(50000) // '1:50K'"
            },
            {
              "name": "zoomToScale",
              "signature": "(zoom: number, latitude?: number) => number",
              "description": "Approximate the scale ratio from zoom level (varies by latitude)"
            },
            {
              "name": "formatPropertyValue",
              "signature": "(value: unknown) => string",
              "description": "Format a property value for display in popups (handles numbers, booleans, arrays)"
            },
            {
              "name": "formatFeatureProperties",
              "signature": "(properties: Record<string, unknown>, options?: { exclude?: string[]; include?: string[]; labelMap?: Record<string, string> }) => string",
              "description": "Create a popup content string from feature properties with optional filtering and label mapping"
            },
            {
              "name": "formatLabel",
              "signature": "(key: string) => string",
              "description": "Format a property key as a human-readable label (handles snake_case and camelCase)"
            }
          ]
        },
        "accessibility": {
          "description": "Accessibility utilities for screen readers",
          "functions": [
            {
              "name": "KEYBOARD_CONTROLS",
              "signature": "Record<string, Record<string, string>>",
              "description": "Keyboard shortcut documentation object for map interactions (pan, zoom, rotate, pitch, reset)"
            },
            {
              "name": "generateKeyboardControlsDescription",
              "signature": "() => string",
              "description": "Generate a text description of keyboard controls for screen readers"
            },
            {
              "name": "generateMapDescription",
              "signature": "(title: string, options?: { center?: LngLat; zoom?: number; featureCount?: number; markerCount?: number; layerTypes?: string[] }) => string",
              "description": "Generate accessible description for a map"
            },
            {
              "name": "generateFeatureCollectionDescription",
              "signature": "(title: string, data: GeoJSONFeatureCollection) => string",
              "description": "Generate accessible description for GeoJSON features (counts and geometry types)"
            },
            {
              "name": "generateMarkersDescription",
              "signature": "(title: string, markers: MarkerData[]) => string",
              "description": "Generate accessible description for markers (count and first few labels)"
            },
            {
              "name": "generateFeatureTable",
              "signature": "(features: GeoJSONFeatureCollection, propertyKeys: string[]) => string",
              "description": "Generate a hidden data table (HTML) for screen readers with sr-only class"
            },
            {
              "name": "generateMarkerList",
              "signature": "(markers: MarkerData[]) => string",
              "description": "Generate a hidden marker list (HTML) for screen readers with sr-only class"
            },
            {
              "name": "generateMoveAnnouncement",
              "signature": "(center: LngLat, zoom: number, action: 'pan' | 'zoom' | 'move') => string",
              "description": "Generate live region announcement for map navigation changes"
            }
          ]
        },
        "styles": {
          "description": "Styling utilities for map layers",
          "functions": [
            {
              "name": "createFillStyle",
              "signature": "(options: { color: ColorExpression; opacity?: NumberExpression; outlineColor?: ColorExpression }) => { paint: Record<string, unknown> }",
              "description": "Create a solid polygon/fill style object for MapLibre layers"
            },
            {
              "name": "createLineStyle",
              "signature": "(options: { color: ColorExpression; width: NumberExpression; opacity?: NumberExpression; dashArray?: number[]; cap?: 'butt' | 'round' | 'square'; join?: 'bevel' | 'round' | 'miter' }) => { paint: Record<string, unknown>; layout: Record<string, unknown> }",
              "description": "Create a line style object with paint and layout properties"
            },
            {
              "name": "createCircleStyle",
              "signature": "(options: { radius: NumberExpression; color: ColorExpression; opacity?: NumberExpression; strokeWidth?: NumberExpression; strokeColor?: ColorExpression; blur?: number }) => { paint: Record<string, unknown> }",
              "description": "Create a circle/point style object for MapLibre layers"
            },
            {
              "name": "createExtrusionStyle",
              "signature": "(options: { height: NumberExpression; baseHeight?: NumberExpression; color: ColorExpression; opacity?: number; verticalGradient?: boolean }) => { paint: Record<string, unknown> }",
              "description": "Create a 3D extrusion style object for building layers"
            },
            {
              "name": "createHeatmapStyle",
              "signature": "(options: { weight?: NumberExpression; intensity?: NumberExpression; radius?: NumberExpression; opacity?: NumberExpression; colorRamp?: string[] }) => { paint: Record<string, unknown> }",
              "description": "Create a heatmap style object with optional color ramp"
            },
            {
              "name": "createHoverExpression",
              "signature": "(baseValue: unknown, hoverValue: unknown, featureStateName?: string) => unknown[]",
              "description": "Create MapLibre expression for hover state colors using feature-state"
            },
            {
              "name": "createSelectionExpression",
              "signature": "(baseValue: unknown, selectedValue: unknown, featureStateName?: string) => unknown[]",
              "description": "Create MapLibre expression for selection state colors using feature-state"
            },
            {
              "name": "createInteractiveExpression",
              "signature": "(baseValue: unknown, hoverValue: unknown, selectedValue: unknown) => unknown[]",
              "description": "Create combined hover and selection expression (selection takes priority)"
            },
            {
              "name": "createCategoricalColorExpression",
              "signature": "(property: string, colorMap: Record<string, string>, defaultColor: string) => unknown[]",
              "description": "Create data-driven color expression based on a property value (match expression)"
            },
            {
              "name": "createContinuousColorExpression",
              "signature": "(property: string, stops: [number, string][], interpolation?: 'linear' | 'exponential' | ['cubic-bezier', number, number, number, number]) => unknown[]",
              "description": "Create continuous color ramp expression for numeric properties"
            },
            {
              "name": "createZoomExpression",
              "signature": "(stops: [number, number][]) => unknown[]",
              "description": "Create zoom-based interpolation expression for size/width values"
            },
            {
              "name": "getHighlightStyle",
              "signature": "(theme?: MapTheme) => { fill: Record<string, unknown>; line: Record<string, unknown>; circle: Record<string, unknown> }",
              "description": "Get pre-configured highlight style properties for hover states"
            },
            {
              "name": "getSelectionStyle",
              "signature": "(theme?: MapTheme) => { fill: Record<string, unknown>; line: Record<string, unknown>; circle: Record<string, unknown> }",
              "description": "Get pre-configured selection style properties with hover and selection states"
            }
          ]
        },
        "bounds": {
          "description": "Bounds calculation utilities for GeoJSON data",
          "functions": [
            {
              "name": "calculateBounds",
              "signature": "(geojson: GeoJSONFeatureCollection | GeoJSONFeature) => BoundsArray | null",
              "description": "Calculate bounds from GeoJSON data, returns [[sw_lng, sw_lat], [ne_lng, ne_lat]] or null"
            },
            {
              "name": "extendBounds",
              "signature": "(bounds: BoundsArray, point: LngLat) => BoundsArray",
              "description": "Extend existing bounds to include a new point"
            },
            {
              "name": "combineBounds",
              "signature": "(bounds1: BoundsArray, bounds2: BoundsArray) => BoundsArray",
              "description": "Combine two bounds into one that encompasses both"
            },
            {
              "name": "isValidBounds",
              "signature": "(bounds: BoundsArray) => boolean",
              "description": "Validate that bounds are properly formed and within valid ranges"
            }
          ]
        },
        "data": {
          "description": "Data loading and validation utilities for GeoJSON",
          "functions": [
            {
              "name": "loadGeoJSON",
              "signature": "(url: string, options?: LoadGeoJSONOptions) => Promise<LoadGeoJSONResult<T>>",
              "description": "Load GeoJSON data from a URL with timeout support and optional validation"
            },
            {
              "name": "validateGeoJSON",
              "signature": "(data: unknown) => ValidationResult",
              "description": "Validate GeoJSON structure, returns { valid, errors, featureCount, geometryTypes }"
            },
            {
              "name": "calculateCentroid",
              "signature": "(geojson: GeoJSONFeatureCollection | GeoJSONFeature) => [number, number] | null",
              "description": "Calculate the centroid of GeoJSON data as [lng, lat]"
            },
            {
              "name": "countFeaturesByType",
              "signature": "(geojson: GeoJSONFeatureCollection) => Record<string, number>",
              "description": "Count features by geometry type (Point, Polygon, etc.)"
            },
            {
              "name": "filterByGeometryType",
              "signature": "(geojson: GeoJSONFeatureCollection, types: string[]) => GeoJSONFeatureCollection",
              "description": "Filter features by geometry type, returns new FeatureCollection"
            }
          ]
        },
        "positioning": {
          "description": "Centralized positioning coordinator for map controls with collision detection, priority-based resolution, and automatic z-index management",
          "functions": [
            {
              "name": "PositioningCoordinator",
              "signature": "class",
              "description": "Centralized manager for map control positioning, layering, and collision avoidance. Manages vertical stacking per position, collision detection across positions, priority-based resolution, and dynamic z-index for expanded states.",
              "example": "const coordinator = createPositioningCoordinator();\ncoordinator.setMapDimensions({ width: 800, height: 600 });\nconst offset = coordinator.register({ id: 'legend-1', type: 'legend', ... });"
            },
            {
              "name": "PositioningCoordinator.register",
              "signature": "(registration: ControlRegistration) => ControlOffset | null",
              "description": "Register a control with the coordinator. Returns computed offset or null if registration failed."
            },
            {
              "name": "PositioningCoordinator.unregister",
              "signature": "(controlId: string) => void",
              "description": "Unregister a control from the coordinator."
            },
            {
              "name": "PositioningCoordinator.update",
              "signature": "(controlId: string, updates: ControlUpdateOptions) => ControlOffset | null",
              "description": "Update a control's registration (dimensions, position, priority, etc.)."
            },
            {
              "name": "PositioningCoordinator.setExpanded",
              "signature": "(controlId: string, isExpanded: boolean) => ControlOffset | null",
              "description": "Set the expanded state of a control (e.g., when a dropdown opens)."
            },
            {
              "name": "PositioningCoordinator.setMapDimensions",
              "signature": "(dimensions: MapDimensions) => void",
              "description": "Set the map dimensions for layout calculations. Triggers recalculation."
            },
            {
              "name": "PositioningCoordinator.subscribe",
              "signature": "(listener: CoordinatorListener) => () => void",
              "description": "Subscribe to coordinator events. Returns unsubscribe function."
            },
            {
              "name": "PositioningCoordinator.startBatch",
              "signature": "() => void",
              "description": "Start batching updates to defer layout recalculation."
            },
            {
              "name": "PositioningCoordinator.endBatch",
              "signature": "() => void",
              "description": "End batching and trigger a single layout recalculation if needed."
            },
            {
              "name": "PositioningCoordinator.getOffset",
              "signature": "(controlId: string) => ControlOffset | null",
              "description": "Get the current computed offset for a control."
            },
            {
              "name": "createPositioningCoordinator",
              "signature": "() => PositioningCoordinator",
              "description": "Factory function to create a new PositioningCoordinator instance."
            },
            {
              "name": "Z_INDEX_LAYERS",
              "signature": "Record<ZIndexLayer, number>",
              "description": "Z-index values for each layer type: { base: 10, interactive: 20, expanded: 30, modal: 40, tooltip: 50 }"
            },
            {
              "name": "PRIORITY_ORDER",
              "signature": "ControlPriority[]",
              "description": "Priority order for collision resolution: ['low', 'normal', 'high', 'critical']"
            },
            {
              "name": "STACK_GAP",
              "signature": "8",
              "description": "Gap between stacked controls in pixels."
            },
            {
              "name": "EDGE_MARGIN",
              "signature": "8",
              "description": "Margin from map edges in pixels."
            },
            {
              "name": "ATTRIBUTION_HEIGHT",
              "signature": "32",
              "description": "Height of the MapLibre attribution control in pixels. Used to offset controls in bottom-right."
            }
          ]
        },
        "cluster": {
          "description": "Cluster utilities for MapLibre GL clustering",
          "functions": [
            {
              "name": "isClusterFeature",
              "signature": "(feature: { properties?: Record<string, unknown> | null }) => boolean",
              "description": "Type guard to check if a feature is a cluster (has cluster_id and point_count)"
            },
            {
              "name": "getClusterExpansionZoom",
              "signature": "(map: Map, sourceId: string, clusterId: number) => Promise<number>",
              "description": "Get the zoom level needed to expand a cluster"
            },
            {
              "name": "getClusterChildren",
              "signature": "(map: Map, sourceId: string, clusterId: number) => Promise<GeoJSONFeature[]>",
              "description": "Get the immediate children features of a cluster"
            },
            {
              "name": "getClusterLeaves",
              "signature": "(map: Map, sourceId: string, clusterId: number, limit?: number, offset?: number) => Promise<GeoJSONFeature[]>",
              "description": "Get all leaf features (individual points) of a cluster"
            },
            {
              "name": "expandCluster",
              "signature": "(map: Map, sourceId: string, clusterId: number, center: LngLat, options?: ClusterExpandOptions) => Promise<void>",
              "description": "Expand a cluster by zooming in to show its contents (main utility for click-to-expand)"
            },
            {
              "name": "getClusterBounds",
              "signature": "(map: Map, sourceId: string, clusterId: number, limit?: number) => Promise<BoundsArray>",
              "description": "Calculate bounds that encompass all leaves of a cluster"
            },
            {
              "name": "expandClusterToBounds",
              "signature": "(map: Map, sourceId: string, clusterId: number, options?: ClusterExpandOptions) => Promise<void>",
              "description": "Expand a cluster by fitting to the bounds of its leaves (alternative to expandCluster)"
            }
          ]
        },
        "lotStyles": {
          "description": "Specialized style utilities for polygon lot visualizations",
          "functions": [
            {
              "name": "createLotFillStyle",
              "signature": "(options: LotFillStyleOptions) => { paint: Record<string, unknown> }",
              "description": "Create fill style for lot polygons with status-based coloring and hover/selection states"
            },
            {
              "name": "createLotOutlineStyle",
              "signature": "(options: LotOutlineStyleOptions) => { paint: Record<string, unknown>; layout: Record<string, unknown> }",
              "description": "Create outline style for lot polygons with zoom-based width scaling"
            },
            {
              "name": "createExtrusionHeightExpression",
              "signature": "(options: ExtrusionHeightOptions) => unknown[]",
              "description": "Create MapLibre expression for 3D lot extrusion heights based on property values"
            },
            {
              "name": "createStatusColorExpression",
              "signature": "(property: string, colorMap: Record<string, string>, defaultColor: string) => unknown[]",
              "description": "Create data-driven color expression based on lot status property"
            },
            {
              "name": "createInteractiveStatusColorExpression",
              "signature": "(property: string, colorMap: Record<string, string>, hoverColor: string, selectedColor: string, defaultColor: string) => unknown[]",
              "description": "Create combined status color expression with hover and selection states using feature-state"
            }
          ]
        },
        "lod": {
          "description": "Level-of-Detail utilities for performance optimization with large datasets",
          "functions": [
            {
              "name": "groupFeaturesByProperty",
              "signature": "(data: GeoJSONFeatureCollection, options: GroupFeaturesOptions) => FeatureGroup[]",
              "description": "Group features by a property value for LOD aggregation"
            },
            {
              "name": "generateConvexHull",
              "signature": "(features: GeoJSONFeature[]) => GeoJSONFeature | null",
              "description": "Generate a convex hull polygon from a set of features"
            },
            {
              "name": "generateBoundingPolygon",
              "signature": "(features: GeoJSONFeature[]) => GeoJSONFeature | null",
              "description": "Generate a bounding box polygon from a set of features"
            },
            {
              "name": "boundsToPolygon",
              "signature": "(bounds: BoundsArray) => GeoJSONFeature",
              "description": "Convert bounds array to a polygon feature"
            },
            {
              "name": "generateLODData",
              "signature": "(data: GeoJSONFeatureCollection, options: GenerateLODOptions) => LODData",
              "description": "Generate multi-level LOD data with aggregated representations at different zoom levels"
            },
            {
              "name": "calculateZoomThresholds",
              "signature": "(featureCount: number, minZoom?: number, maxZoom?: number) => number[]",
              "description": "Calculate optimal zoom thresholds based on feature count"
            },
            {
              "name": "simplifyPolygon",
              "signature": "(coordinates: number[][][], tolerance: number) => number[][][]",
              "description": "Simplify polygon coordinates using Douglas-Peucker algorithm"
            },
            {
              "name": "simplifyFeature",
              "signature": "(feature: GeoJSONFeature, tolerance: number) => GeoJSONFeature",
              "description": "Simplify a single feature's geometry"
            },
            {
              "name": "simplifyFeatureCollection",
              "signature": "(data: GeoJSONFeatureCollection, tolerance: number) => GeoJSONFeatureCollection",
              "description": "Simplify all features in a collection"
            },
            {
              "name": "simplifyFeatureCollectionWithStats",
              "signature": "(data: GeoJSONFeatureCollection, tolerance: number) => { data: GeoJSONFeatureCollection; stats: SimplificationStats }",
              "description": "Simplify collection and return statistics (point reduction, time)"
            },
            {
              "name": "generateLODDataWithSimplification",
              "signature": "(data: GeoJSONFeatureCollection, options: GenerateLODOptions & { simplificationThresholds?: SimplificationThreshold[] }) => LODDataWithSimplification",
              "description": "Generate LOD data with automatic polygon simplification at each level"
            },
            {
              "name": "getSimplifiedFeaturesForZoom",
              "signature": "(lodData: LODDataWithSimplification, zoom: number) => GeoJSONFeatureCollection",
              "description": "Get the appropriate simplified features for a given zoom level"
            },
            {
              "name": "createDynamicLODManager",
              "signature": "(data: GeoJSONFeatureCollection, options?: DynamicLODOptions) => DynamicLODState",
              "description": "Create a manager for dynamic LOD switching based on zoom and performance"
            },
            {
              "name": "getSimplificationToleranceForZoom",
              "signature": "(zoom: number, thresholds?: SimplificationThreshold[]) => number",
              "description": "Get appropriate simplification tolerance for a zoom level"
            },
            {
              "name": "DEFAULT_SIMPLIFICATION_THRESHOLDS",
              "signature": "SimplificationThreshold[]",
              "description": "Default zoom-based simplification thresholds"
            }
          ]
        },
        "spatial": {
          "description": "Spatial indexing utilities for efficient viewport queries on large datasets",
          "functions": [
            {
              "name": "buildSpatialIndex",
              "signature": "(data: GeoJSONFeatureCollection, options?: BuildSpatialIndexOptions) => SpatialIndex",
              "description": "Build a grid-based spatial index for fast viewport queries"
            },
            {
              "name": "queryByBounds",
              "signature": "(index: SpatialIndex, bounds: BoundsArray, options?: QuerySpatialIndexOptions) => GeoJSONFeatureCollection",
              "description": "Query features within bounds from a spatial index"
            },
            {
              "name": "queryByBoundsWithStats",
              "signature": "(index: SpatialIndex, bounds: BoundsArray, options?: QuerySpatialIndexOptions) => { data: GeoJSONFeatureCollection; stats: QueryStats }",
              "description": "Query features with performance statistics (cells checked, culled percentage)"
            },
            {
              "name": "createViewportQuery",
              "signature": "(index: SpatialIndex) => (bounds: BoundsArray) => GeoJSONFeatureCollection",
              "description": "Create a reusable viewport query function"
            },
            {
              "name": "createViewportManager",
              "signature": "(data: GeoJSONFeatureCollection, options?: ViewportManagedDataOptions) => ViewportManagedResult",
              "description": "Create a managed viewport system that automatically updates on map move"
            },
            {
              "name": "buildRTreeIndex",
              "signature": "(data: GeoJSONFeatureCollection, options?: BuildRTreeIndexOptions) => RTreeIndex",
              "description": "Build an R-tree spatial index (better for clustered/overlapping data)"
            },
            {
              "name": "queryRTreeByBounds",
              "signature": "(index: RTreeIndex, bounds: BoundsArray, options?: QuerySpatialIndexOptions) => GeoJSONFeatureCollection",
              "description": "Query features within bounds from an R-tree index"
            },
            {
              "name": "queryRTreeByBoundsWithStats",
              "signature": "(index: RTreeIndex, bounds: BoundsArray, options?: QuerySpatialIndexOptions) => { data: GeoJSONFeatureCollection; stats: RTreeQueryStats }",
              "description": "Query R-tree with performance statistics"
            },
            {
              "name": "createRTreeViewportManager",
              "signature": "(data: GeoJSONFeatureCollection, options?: ViewportManagedDataOptions) => ViewportManagedResult",
              "description": "Create a managed viewport system using R-tree index"
            },
            {
              "name": "createPredictiveViewportManager",
              "signature": "(data: GeoJSONFeatureCollection, options?: ViewportManagedDataOptions) => PredictiveViewportResult",
              "description": "Create viewport manager with predictive pan loading to eliminate pop-in"
            },
            {
              "name": "createIdleViewportManager",
              "signature": "(data: GeoJSONFeatureCollection, options?: IdleViewportManagerOptions) => IdleViewportResult",
              "description": "Create viewport manager that defers updates to idle time for smooth panning"
            }
          ]
        },
        "fingerprint": {
          "description": "Fast O(1) GeoJSON equality checking via fingerprinting",
          "functions": [
            {
              "name": "createGeoJSONFingerprint",
              "signature": "(data: GeoJSONFeatureCollection | GeoJSON.GeoJSON | string | null | undefined) => GeoJSONFingerprint",
              "description": "Create a lightweight fingerprint for fast equality comparison without deep comparison"
            },
            {
              "name": "fingerprintEqual",
              "signature": "(a: GeoJSONFingerprint | null, b: GeoJSONFingerprint | null) => boolean",
              "description": "Compare two fingerprints for equality"
            },
            {
              "name": "geoJSONEqual",
              "signature": "(a: GeoJSONFeatureCollection | null | undefined, b: GeoJSONFeatureCollection | null | undefined) => boolean",
              "description": "Check if two GeoJSON collections are equal using fingerprinting"
            }
          ]
        },
        "equality": {
          "description": "Deep equality and delta detection utilities for efficient updates",
          "functions": [
            {
              "name": "deepEqual",
              "signature": "(a: unknown, b: unknown) => boolean",
              "description": "Deep equality comparison optimized for MapLibre expressions and style objects"
            },
            {
              "name": "paintPropsEqual",
              "signature": "(a: PaintProps | undefined, b: PaintProps | undefined) => boolean",
              "description": "Check if two paint property objects are equal"
            },
            {
              "name": "getChangedPaintProps",
              "signature": "(oldProps: PaintProps | undefined, newProps: PaintProps | undefined) => PaintPropsDiff",
              "description": "Get only the changed paint properties (for efficient MapLibre updates)"
            },
            {
              "name": "filterEqual",
              "signature": "(a: FilterExpression | undefined, b: FilterExpression | undefined) => boolean",
              "description": "Check if two filter expressions are equal"
            },
            {
              "name": "serializeFilter",
              "signature": "(filter: FilterExpression | undefined) => string",
              "description": "Serialize a filter expression for comparison"
            }
          ]
        },
        "basemaps": {
          "description": "Basemap registry, presets, and utilities for basemap switching",
          "functions": [
            {
              "name": "resolveBasemapStyle",
              "signature": "(basemap: BasemapDefinition, apiKeys?: BasemapApiKeys) => string | StyleSpecification | null",
              "description": "Resolve a basemap's style spec, injecting API key if needed. Returns null if required key is missing."
            },
            {
              "name": "getAvailableBasemaps",
              "signature": "(basemaps: BasemapDefinition[], apiKeys?: BasemapApiKeys) => BasemapDefinition[]",
              "description": "Filter basemaps to only those with available API keys. Free basemaps always included."
            },
            {
              "name": "resolveBasemapThumbnail",
              "signature": "(basemap: BasemapDefinition, apiKeys?: BasemapApiKeys) => string",
              "description": "Resolve thumbnail URL, appending provider-specific API key query parameter if needed."
            }
          ],
          "constants": [
            {
              "name": "FREE_BASEMAPS",
              "type": "BasemapDefinition[]",
              "description": "3 free basemaps: OSM Standard, Classic Light, Classic Dark (no API key required)"
            },
            {
              "name": "BUILTIN_BASEMAPS",
              "type": "BasemapDefinition[]",
              "description": "All 12 built-in basemaps including MapTiler (4) and Stadia Maps (5) options"
            },
            {
              "name": "OSM_STANDARD",
              "type": "BasemapDefinition",
              "description": "OpenStreetMap raster tiles (free)"
            },
            {
              "name": "CLASSIC_LIGHT",
              "type": "BasemapDefinition",
              "description": "Classic Theme light vector style (free)"
            },
            {
              "name": "CLASSIC_DARK",
              "type": "BasemapDefinition",
              "description": "Classic Theme dark vector style (free)"
            },
            {
              "name": "MAPTILER_STREETS",
              "type": "BasemapDefinition",
              "description": "MapTiler Streets (requires API key)"
            },
            {
              "name": "MAPTILER_SATELLITE",
              "type": "BasemapDefinition",
              "description": "MapTiler Satellite (requires API key)"
            },
            {
              "name": "MAPTILER_OUTDOOR",
              "type": "BasemapDefinition",
              "description": "MapTiler Outdoor (requires API key)"
            },
            {
              "name": "MAPTILER_BASIC",
              "type": "BasemapDefinition",
              "description": "MapTiler Basic (requires API key)"
            },
            {
              "name": "STADIA_ALIDADE_SMOOTH",
              "type": "BasemapDefinition",
              "description": "Stadia Alidade Smooth (requires API key)"
            },
            {
              "name": "STADIA_ALIDADE_DARK",
              "type": "BasemapDefinition",
              "description": "Stadia Alidade Dark (requires API key)"
            },
            {
              "name": "STADIA_STAMEN_TONER",
              "type": "BasemapDefinition",
              "description": "Stamen Toner artistic style (requires API key)"
            },
            {
              "name": "STADIA_STAMEN_WATERCOLOR",
              "type": "BasemapDefinition",
              "description": "Stamen Watercolor artistic style (requires API key)"
            },
            {
              "name": "STADIA_STAMEN_TERRAIN",
              "type": "BasemapDefinition",
              "description": "Stamen Terrain outdoor style (requires API key)"
            }
          ]
        },
        "colors": {
          "description": "Pre-defined color palettes and MapLibre expressions for real estate and map feature visualization",
          "submodules": {
            "realEstate": {
              "description": "Real estate domain colors for lot and inventory visualization",
              "constants": [
                {
                  "name": "lotAvailabilityColors",
                  "type": "Record<LotAvailability, string>",
                  "description": "Colors for lot availability: Available (green), Future (brown), Spec (yellow), Model (lavender), Under Contract (light blue), Sold (red), Unavailable (gray)"
                },
                {
                  "name": "inventoryStatusColors",
                  "type": "Record<InventoryStatus, string>",
                  "description": "Colors for inventory status: Ready Now (green), Upcoming (orange), default (gray)"
                },
                {
                  "name": "lotSizeColors",
                  "type": "Record<LotSize, string>",
                  "description": "Colors for lot size: Small (blue), Medium (green), Large (orange), Extra Large (red), Unknown (gray)"
                }
              ],
              "functions": [
                {
                  "name": "getContrastTextColor",
                  "signature": "(backgroundColor: string) => string",
                  "description": "Get WCAG-compliant contrast text color (black or white) for a background color"
                },
                {
                  "name": "getAvailabilityColor",
                  "signature": "(availability: string) => string",
                  "description": "Get color for lot availability status, returns Unknown color for invalid values"
                },
                {
                  "name": "getSizeColor",
                  "signature": "(size: string) => string",
                  "description": "Get color for lot size category, returns Unknown color for invalid values"
                }
              ],
              "types": [
                {
                  "name": "LotAvailability",
                  "description": "Available | Future | Spec | Model | Under Contract | Sold | Unavailable | Unknown"
                },
                {
                  "name": "InventoryStatus",
                  "description": "Ready Now | Upcoming | default"
                },
                {
                  "name": "LotSize",
                  "description": "Small | Medium | Large | Extra Large | Unknown"
                }
              ]
            },
            "mapFeatures": {
              "description": "Colors for common map visualization features",
              "constants": [
                {
                  "name": "clusterColors",
                  "type": "{ small: string; medium: string; large: string; stroke: string; text: string }",
                  "description": "Cluster visualization colors using brand tokens: small (<10 points, teal), medium (10-50, navy), large (50+, plum)"
                },
                {
                  "name": "boundaryColors",
                  "type": "{ fill: string; line: string; hoverFill: string; defaultFill: string; hoverLine: string; defaultLine: string }",
                  "description": "Colors for community/region boundary features"
                },
                {
                  "name": "salesOfficeColor",
                  "type": "string",
                  "description": "Color for sales office markers (brand red #C50F22)"
                },
                {
                  "name": "nonMatchedColor",
                  "type": "string",
                  "description": "Color for de-emphasized/non-matched features (gray #9e9e9e)"
                }
              ]
            },
            "expressions": {
              "description": "Pre-built MapLibre color expressions for data-driven styling",
              "constants": [
                {
                  "name": "lotAvailabilityColorExpression",
                  "type": "unknown[]",
                  "description": "MapLibre match expression for 'availability' property using lotAvailabilityColors"
                },
                {
                  "name": "inventoryStatusColorExpression",
                  "type": "unknown[]",
                  "description": "MapLibre match expression for 'status' property using inventoryStatusColors"
                },
                {
                  "name": "lotSizeColorExpression",
                  "type": "unknown[]",
                  "description": "MapLibre match expression for 'sizeCategory' property using lotSizeColors"
                },
                {
                  "name": "clusterColorExpression",
                  "type": "unknown[]",
                  "description": "MapLibre step expression for point_count using clusterColors (small <10, medium 10-50, large 50+)"
                }
              ],
              "functions": [
                {
                  "name": "createMatchedColorExpression",
                  "signature": "(baseColorExpression: unknown[], matchedProperty?: string) => unknown[]",
                  "description": "Wrap a color expression with matched/non-matched logic. Uses nonMatchedColor for features where matchedProperty is false."
                }
              ]
            }
          },
          "example": "import { lotAvailabilityColorExpression, clusterColorExpression, getContrastTextColor } from '@classic-homes/maps-core';\n\n// Use pre-built expressions in layers\n<FillLayer fillColor={lotAvailabilityColorExpression} />\n<CircleLayer circleColor={clusterColorExpression} />\n\n// Get contrast text for labels\nconst textColor = getContrastTextColor('#4caf50'); // Returns white"
        }
      }
    },
    "workers": {
      "importPath": "@classic-homes/maps-core/workers",
      "description": "Web Worker infrastructure for off-main-thread spatial processing",
      "modules": {
        "client": {
          "description": "Client API for communicating with spatial workers",
          "functions": [
            {
              "name": "SpatialWorkerClient",
              "signature": "class",
              "description": "Client class for interacting with the spatial worker. Handles worker lifecycle, request tracking, and graceful degradation."
            },
            {
              "name": "createSpatialWorkerClient",
              "signature": "(options?: SpatialWorkerClientOptions) => SpatialWorkerClient",
              "description": "Factory function to create a spatial worker client"
            }
          ],
          "example": "// Vite setup\nimport SpatialWorker from '@classic-homes/maps-core/workers/spatial.worker?worker';\nconst client = createSpatialWorkerClient({\n  workerFactory: () => new SpatialWorker(),\n  useRTree: true,\n});\n\nawait client.initialize(geojsonData);\nconst result = await client.query(viewportBounds);\nclient.terminate();"
        }
      }
    },
    "theme": {
      "importPath": "@classic-homes/maps-core/theme",
      "description": "Theme exports only (for tree-shaking)"
    },
    "types": {
      "importPath": "@classic-homes/maps-core/types",
      "description": "TypeScript type definitions only"
    }
  },
  "types": {
    "props": {
      "description": "Component prop types",
      "types": [
        {
          "name": "BaseMapProps",
          "description": "Base props shared by all map components",
          "properties": [
            {
              "name": "title",
              "type": "string",
              "required": true,
              "description": "Accessible title for the map (aria-label)"
            },
            {
              "name": "description",
              "type": "string",
              "required": false,
              "description": "Optional description for screen readers"
            },
            {
              "name": "width",
              "type": "number | string",
              "required": false,
              "description": "Map width (CSS value or pixels)"
            },
            {
              "name": "height",
              "type": "number | string",
              "required": false,
              "description": "Map height (default: 400)"
            },
            {
              "name": "minHeight",
              "type": "number",
              "required": false,
              "description": "Minimum height constraint"
            },
            {
              "name": "maxHeight",
              "type": "number",
              "required": false,
              "description": "Maximum height constraint"
            },
            {
              "name": "loading",
              "type": "boolean",
              "required": false,
              "description": "Show loading skeleton"
            },
            {
              "name": "error",
              "type": "string | null",
              "required": false,
              "description": "Error message to display"
            },
            {
              "name": "emptyMessage",
              "type": "string",
              "required": false,
              "description": "Message when no data/features"
            },
            {
              "name": "theme",
              "type": "'light' | 'dark' | 'auto'",
              "required": false,
              "description": "Theme preference"
            },
            {
              "name": "animation",
              "type": "boolean",
              "required": false,
              "description": "Enable animations (respects prefers-reduced-motion)"
            },
            {
              "name": "class",
              "type": "string",
              "required": false,
              "description": "Custom CSS class"
            }
          ]
        },
        {
          "name": "MapProps",
          "description": "Full map component props (extends BaseMapProps)",
          "properties": [
            {
              "name": "style",
              "type": "string | MapStyleSpec",
              "required": false,
              "description": "Map style URL or style spec object"
            },
            {
              "name": "center",
              "type": "[number, number]",
              "required": false,
              "description": "Initial center [lng, lat]"
            },
            {
              "name": "zoom",
              "type": "number",
              "required": false,
              "description": "Initial zoom level (0-24)"
            },
            {
              "name": "bearing",
              "type": "number",
              "required": false,
              "description": "Initial bearing in degrees"
            },
            {
              "name": "pitch",
              "type": "number",
              "required": false,
              "description": "Initial pitch/tilt in degrees"
            },
            {
              "name": "bounds",
              "type": "[[number, number], [number, number]]",
              "required": false,
              "description": "Fit to bounds on load"
            },
            {
              "name": "boundsPadding",
              "type": "number | { top, right, bottom, left }",
              "required": false,
              "description": "Padding when fitting to bounds"
            },
            {
              "name": "minZoom",
              "type": "number",
              "required": false,
              "description": "Minimum zoom level"
            },
            {
              "name": "maxZoom",
              "type": "number",
              "required": false,
              "description": "Maximum zoom level"
            },
            {
              "name": "maxBounds",
              "type": "BoundsArray",
              "required": false,
              "description": "Maximum bounds the map can pan to"
            },
            {
              "name": "interactive",
              "type": "boolean",
              "required": false,
              "description": "Enable map interactions"
            },
            {
              "name": "scrollZoom",
              "type": "boolean",
              "required": false,
              "description": "Enable scroll zoom"
            },
            {
              "name": "dragPan",
              "type": "boolean",
              "required": false,
              "description": "Enable drag pan"
            },
            {
              "name": "dragRotate",
              "type": "boolean",
              "required": false,
              "description": "Enable drag rotate"
            },
            {
              "name": "doubleClickZoom",
              "type": "boolean",
              "required": false,
              "description": "Enable double click zoom"
            },
            {
              "name": "touchZoomRotate",
              "type": "boolean",
              "required": false,
              "description": "Enable touch zoom/rotate"
            },
            {
              "name": "keyboard",
              "type": "boolean",
              "required": false,
              "description": "Enable keyboard navigation"
            },
            {
              "name": "navigationControl",
              "type": "boolean",
              "required": false,
              "description": "Show navigation control"
            },
            {
              "name": "navigationControlPosition",
              "type": "ControlPosition",
              "required": false,
              "description": "Navigation control position"
            },
            {
              "name": "scaleControl",
              "type": "boolean",
              "required": false,
              "description": "Show scale control"
            },
            {
              "name": "scaleControlPosition",
              "type": "ControlPosition",
              "required": false,
              "description": "Scale control position"
            },
            {
              "name": "fullscreenControl",
              "type": "boolean",
              "required": false,
              "description": "Show fullscreen control"
            },
            {
              "name": "geolocateControl",
              "type": "boolean",
              "required": false,
              "description": "Show geolocate control"
            },
            {
              "name": "attributionControl",
              "type": "boolean",
              "required": false,
              "description": "Show attribution control"
            },
            {
              "name": "customAttribution",
              "type": "string",
              "required": false,
              "description": "Custom attribution text"
            },
            {
              "name": "fullscreenControlPosition",
              "type": "ControlPosition",
              "required": false,
              "description": "Fullscreen control position"
            },
            {
              "name": "geolocateControlPosition",
              "type": "ControlPosition",
              "required": false,
              "description": "Geolocate control position"
            },
            {
              "name": "onLoad",
              "type": "(params: MapLoadEventParams) => void",
              "required": false,
              "description": "Callback when map loads"
            },
            {
              "name": "onClick",
              "type": "(params: MapClickEventParams) => void",
              "required": false,
              "description": "Callback for map click"
            },
            {
              "name": "onDblClick",
              "type": "(params: MapClickEventParams) => void",
              "required": false,
              "description": "Callback for map double click"
            },
            {
              "name": "onContextMenu",
              "type": "(params: MapClickEventParams) => void",
              "required": false,
              "description": "Callback for map right click"
            },
            {
              "name": "onMouseMove",
              "type": "(params: MapHoverEventParams) => void",
              "required": false,
              "description": "Callback for mouse move"
            },
            {
              "name": "onMove",
              "type": "(params: MapMoveEventParams) => void",
              "required": false,
              "description": "Callback for map move/pan"
            },
            {
              "name": "onMoveStart",
              "type": "(params: MapMoveEventParams) => void",
              "required": false,
              "description": "Callback when move starts"
            },
            {
              "name": "onMoveEnd",
              "type": "(params: MapMoveEventParams) => void",
              "required": false,
              "description": "Callback when move ends"
            },
            {
              "name": "onZoom",
              "type": "(params: MapMoveEventParams) => void",
              "required": false,
              "description": "Callback for zoom change"
            },
            {
              "name": "onError",
              "type": "(params: MapErrorEventParams) => void",
              "required": false,
              "description": "Callback for map errors"
            }
          ]
        },
        {
          "name": "SourceProps",
          "description": "Props for GeoJSON/vector/raster data sources",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Unique source ID"
            },
            {
              "name": "type",
              "type": "'geojson' | 'vector' | 'raster' | 'raster-dem' | 'image' | 'video'",
              "required": true,
              "description": "Source type"
            },
            {
              "name": "data",
              "type": "GeoJSONFeatureCollection | string",
              "required": false,
              "description": "Source data (for GeoJSON sources)"
            },
            {
              "name": "tiles",
              "type": "string[]",
              "required": false,
              "description": "Tile URL template (for vector/raster)"
            },
            {
              "name": "url",
              "type": "string",
              "required": false,
              "description": "TileJSON URL"
            },
            {
              "name": "tileSize",
              "type": "number",
              "required": false,
              "description": "Tile size (default: 512)"
            },
            {
              "name": "attribution",
              "type": "string",
              "required": false,
              "description": "Attribution text"
            },
            {
              "name": "cluster",
              "type": "boolean | ClusterConfig",
              "required": false,
              "description": "Enable clustering (GeoJSON only)"
            },
            {
              "name": "clusterRadius",
              "type": "number",
              "required": false,
              "description": "Cluster radius (GeoJSON only)"
            },
            {
              "name": "clusterMaxZoom",
              "type": "number",
              "required": false,
              "description": "Max zoom for clustering"
            },
            {
              "name": "generateId",
              "type": "boolean",
              "required": false,
              "description": "Generate feature IDs"
            },
            {
              "name": "fitBounds",
              "type": "boolean",
              "required": false,
              "description": "Fit map to source bounds when loaded"
            },
            {
              "name": "fitBoundsPadding",
              "type": "number | { top, right, bottom, left }",
              "required": false,
              "description": "Padding when fitting"
            },
            {
              "name": "fitBoundsMaxZoom",
              "type": "number",
              "required": false,
              "description": "Max zoom when fitting"
            }
          ]
        },
        {
          "name": "MarkerProps",
          "description": "Props for map markers. NOTE: DOM-based markers (maplibregl.Marker) are susceptible to CSS framework interference that can cause visual sliding during map pan/rotate operations. For production use cases requiring markers, consider using SymbolLayer instead, which renders on the GPU canvas and is immune to CSS interference.",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": false,
              "description": "Unique marker ID"
            },
            {
              "name": "coordinates",
              "type": "[number, number]",
              "required": true,
              "description": "Marker position [lng, lat]"
            },
            {
              "name": "color",
              "type": "string",
              "required": false,
              "description": "Marker color"
            },
            {
              "name": "anchor",
              "type": "'center' | 'top' | 'bottom' | 'left' | 'right' | ...",
              "required": false,
              "description": "Custom anchor point"
            },
            {
              "name": "offset",
              "type": "[number, number]",
              "required": false,
              "description": "Offset from coordinates in pixels"
            },
            {
              "name": "rotation",
              "type": "number",
              "required": false,
              "description": "Marker rotation angle"
            },
            {
              "name": "draggable",
              "type": "boolean",
              "required": false,
              "description": "Enable dragging"
            },
            {
              "name": "scale",
              "type": "number",
              "required": false,
              "description": "Marker scale"
            },
            {
              "name": "onClick",
              "type": "(params: MarkerClickEventParams) => void",
              "required": false,
              "description": "Callback for marker click"
            },
            {
              "name": "onDrag",
              "type": "(params: MarkerDragEventParams) => void",
              "required": false,
              "description": "Callback for marker drag"
            },
            {
              "name": "onDragEnd",
              "type": "(params: MarkerDragEventParams) => void",
              "required": false,
              "description": "Callback when drag ends"
            }
          ]
        },
        {
          "name": "PopupProps",
          "description": "Props for map popups",
          "properties": [
            {
              "name": "coordinates",
              "type": "[number, number]",
              "required": true,
              "description": "Popup position [lng, lat]"
            },
            {
              "name": "closeButton",
              "type": "boolean",
              "required": false,
              "description": "Show close button (default: true)"
            },
            {
              "name": "closeOnClick",
              "type": "boolean",
              "required": false,
              "description": "Close on click outside (default: true)"
            },
            {
              "name": "closeOnMove",
              "type": "boolean",
              "required": false,
              "description": "Close when map moves"
            },
            {
              "name": "anchor",
              "type": "'center' | 'top' | 'bottom' | 'left' | 'right' | ...",
              "required": false,
              "description": "Popup anchor"
            },
            {
              "name": "offset",
              "type": "number | [number, number]",
              "required": false,
              "description": "Popup offset"
            },
            {
              "name": "maxWidth",
              "type": "string",
              "required": false,
              "description": "Maximum width (default: '300px')"
            },
            {
              "name": "focusAfterOpen",
              "type": "boolean",
              "required": false,
              "description": "Focus popup when opened"
            },
            {
              "name": "autoPan",
              "type": "boolean",
              "required": false,
              "description": "Auto-pan to ensure popup is visible (default: true)"
            },
            {
              "name": "autoPanPadding",
              "type": "number | { top, right, bottom, left }",
              "required": false,
              "description": "Padding around popup when auto-panning"
            },
            {
              "name": "onOpen",
              "type": "(params: PopupEventParams) => void",
              "required": false,
              "description": "Callback when popup opens"
            },
            {
              "name": "onClose",
              "type": "(params: PopupEventParams) => void",
              "required": false,
              "description": "Callback when popup closes"
            }
          ]
        },
        {
          "name": "BaseLayerProps",
          "description": "Base props shared by all layer components",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Unique layer ID"
            },
            {
              "name": "source",
              "type": "string",
              "required": true,
              "description": "Source ID this layer uses"
            },
            {
              "name": "sourceLayer",
              "type": "string",
              "required": false,
              "description": "Source layer (for vector tile sources)"
            },
            {
              "name": "visible",
              "type": "boolean",
              "required": false,
              "description": "Layer visibility"
            },
            {
              "name": "opacity",
              "type": "number",
              "required": false,
              "description": "Layer opacity (0-1)"
            },
            {
              "name": "minZoom",
              "type": "number",
              "required": false,
              "description": "Minimum zoom level"
            },
            {
              "name": "maxZoom",
              "type": "number",
              "required": false,
              "description": "Maximum zoom level"
            },
            {
              "name": "filter",
              "type": "unknown[]",
              "required": false,
              "description": "Filter expression for features"
            },
            {
              "name": "beforeId",
              "type": "string",
              "required": false,
              "description": "Insert layer before another"
            },
            {
              "name": "onClick",
              "type": "(params: FeatureClickEventParams) => void",
              "required": false,
              "description": "Callback for feature click"
            },
            {
              "name": "onHover",
              "type": "(params: FeatureHoverEventParams) => void",
              "required": false,
              "description": "Callback for feature hover"
            },
            {
              "name": "hoverThrottleMs",
              "type": "number",
              "required": false,
              "description": "Throttle interval for onHover callback in ms. Set to 0 for no throttling. Feature-state updates remain immediate, only callback is throttled."
            }
          ]
        },
        {
          "name": "FillLayerProps",
          "description": "Props for polygon fill layers (extends BaseLayerProps)",
          "properties": [
            {
              "name": "fillColor",
              "type": "string | Expression",
              "required": false,
              "description": "Fill color"
            },
            {
              "name": "fillOpacity",
              "type": "number | Expression",
              "required": false,
              "description": "Fill opacity (0-1)"
            },
            {
              "name": "fillOutlineColor",
              "type": "string | Expression",
              "required": false,
              "description": "Outline color"
            },
            {
              "name": "fillPattern",
              "type": "string",
              "required": false,
              "description": "Fill pattern (sprite image name)"
            },
            {
              "name": "fillTranslate",
              "type": "[number, number]",
              "required": false,
              "description": "Translate fill geometry"
            },
            {
              "name": "fillAntialias",
              "type": "boolean",
              "required": false,
              "description": "Fill antialias"
            }
          ]
        },
        {
          "name": "LineLayerProps",
          "description": "Props for line layers (extends BaseLayerProps)",
          "properties": [
            {
              "name": "lineColor",
              "type": "string | Expression",
              "required": false,
              "description": "Line color"
            },
            {
              "name": "lineWidth",
              "type": "number | Expression",
              "required": false,
              "description": "Line width in pixels"
            },
            {
              "name": "lineOpacity",
              "type": "number | Expression",
              "required": false,
              "description": "Line opacity (0-1)"
            },
            {
              "name": "lineCap",
              "type": "'butt' | 'round' | 'square'",
              "required": false,
              "description": "Line cap style"
            },
            {
              "name": "lineJoin",
              "type": "'bevel' | 'round' | 'miter'",
              "required": false,
              "description": "Line join style"
            },
            {
              "name": "lineDasharray",
              "type": "number[]",
              "required": false,
              "description": "Dash pattern"
            },
            {
              "name": "lineGapWidth",
              "type": "number",
              "required": false,
              "description": "Line gap width"
            },
            {
              "name": "lineOffset",
              "type": "number",
              "required": false,
              "description": "Line offset"
            }
          ]
        },
        {
          "name": "CircleLayerProps",
          "description": "Props for circle point layers (extends BaseLayerProps)",
          "properties": [
            {
              "name": "circleRadius",
              "type": "number | Expression",
              "required": false,
              "description": "Circle radius in pixels"
            },
            {
              "name": "circleColor",
              "type": "string | Expression",
              "required": false,
              "description": "Circle color"
            },
            {
              "name": "circleOpacity",
              "type": "number | Expression",
              "required": false,
              "description": "Circle opacity (0-1)"
            },
            {
              "name": "circleStrokeWidth",
              "type": "number | Expression",
              "required": false,
              "description": "Stroke width"
            },
            {
              "name": "circleStrokeColor",
              "type": "string | Expression",
              "required": false,
              "description": "Stroke color"
            },
            {
              "name": "circleBlur",
              "type": "number",
              "required": false,
              "description": "Circle blur"
            },
            {
              "name": "circlePitchAlignment",
              "type": "'map' | 'viewport'",
              "required": false,
              "description": "Pitch alignment"
            }
          ]
        },
        {
          "name": "SymbolLayerProps",
          "description": "Props for symbol/label layers (extends BaseLayerProps)",
          "properties": [
            {
              "name": "iconImage",
              "type": "string | Expression",
              "required": false,
              "description": "Icon image name (from sprite)"
            },
            {
              "name": "iconSize",
              "type": "number | Expression",
              "required": false,
              "description": "Icon size"
            },
            {
              "name": "iconRotate",
              "type": "number | Expression",
              "required": false,
              "description": "Icon rotation angle"
            },
            {
              "name": "iconOffset",
              "type": "[number, number]",
              "required": false,
              "description": "Icon offset"
            },
            {
              "name": "iconAnchor",
              "type": "'center' | 'left' | 'right' | 'top' | 'bottom' | ...",
              "required": false,
              "description": "Icon anchor"
            },
            {
              "name": "iconAllowOverlap",
              "type": "boolean",
              "required": false,
              "description": "Allow icon overlap"
            },
            {
              "name": "textField",
              "type": "string | Expression",
              "required": false,
              "description": "Text field content"
            },
            {
              "name": "textFont",
              "type": "string[]",
              "required": false,
              "description": "Text font stack"
            },
            {
              "name": "textSize",
              "type": "number | Expression",
              "required": false,
              "description": "Text size"
            },
            {
              "name": "textColor",
              "type": "string | Expression",
              "required": false,
              "description": "Text color"
            },
            {
              "name": "textHaloColor",
              "type": "string",
              "required": false,
              "description": "Text halo color"
            },
            {
              "name": "textHaloWidth",
              "type": "number",
              "required": false,
              "description": "Text halo width"
            },
            {
              "name": "textAnchor",
              "type": "'center' | 'left' | 'right' | 'top' | 'bottom' | ...",
              "required": false,
              "description": "Text anchor"
            },
            {
              "name": "symbolPlacement",
              "type": "'point' | 'line' | 'line-center'",
              "required": false,
              "description": "Symbol placement"
            }
          ]
        },
        {
          "name": "FillExtrusionLayerProps",
          "description": "Props for 3D extrusion layers (extends BaseLayerProps)",
          "properties": [
            {
              "name": "fillExtrusionHeight",
              "type": "number | Expression",
              "required": true,
              "description": "Extrusion height in meters"
            },
            {
              "name": "fillExtrusionBase",
              "type": "number | Expression",
              "required": false,
              "description": "Base height in meters"
            },
            {
              "name": "fillExtrusionColor",
              "type": "string | Expression",
              "required": false,
              "description": "Extrusion color"
            },
            {
              "name": "fillExtrusionOpacity",
              "type": "number",
              "required": false,
              "description": "Extrusion opacity (0-1)"
            },
            {
              "name": "fillExtrusionVerticalGradient",
              "type": "boolean",
              "required": false,
              "description": "Enable vertical gradient"
            }
          ]
        },
        {
          "name": "HeatmapLayerProps",
          "description": "Props for heatmap layers (extends BaseLayerProps)",
          "properties": [
            {
              "name": "heatmapRadius",
              "type": "number | Expression",
              "required": false,
              "description": "Heatmap radius in pixels"
            },
            {
              "name": "heatmapIntensity",
              "type": "number | Expression",
              "required": false,
              "description": "Intensity multiplier"
            },
            {
              "name": "heatmapWeight",
              "type": "number | Expression",
              "required": false,
              "description": "Weight per point"
            },
            {
              "name": "heatmapColor",
              "type": "Expression",
              "required": false,
              "description": "Heatmap color ramp expression"
            },
            {
              "name": "heatmapOpacity",
              "type": "number | Expression",
              "required": false,
              "description": "Heatmap opacity"
            }
          ]
        },
        {
          "name": "RasterLayerProps",
          "description": "Props for raster/image layers (extends BaseLayerProps)",
          "properties": [
            {
              "name": "rasterOpacity",
              "type": "number",
              "required": false,
              "description": "Raster opacity (0-1)"
            },
            {
              "name": "rasterHueRotate",
              "type": "number",
              "required": false,
              "description": "Hue rotation in degrees"
            },
            {
              "name": "rasterBrightnessMin",
              "type": "number",
              "required": false,
              "description": "Minimum brightness (0-1)"
            },
            {
              "name": "rasterBrightnessMax",
              "type": "number",
              "required": false,
              "description": "Maximum brightness (0-1)"
            },
            {
              "name": "rasterSaturation",
              "type": "number",
              "required": false,
              "description": "Saturation adjustment (-1 to 1)"
            },
            {
              "name": "rasterContrast",
              "type": "number",
              "required": false,
              "description": "Contrast adjustment (-1 to 1)"
            },
            {
              "name": "rasterResampling",
              "type": "'linear' | 'nearest'",
              "required": false,
              "description": "Resampling method"
            },
            {
              "name": "rasterFadeDuration",
              "type": "number",
              "required": false,
              "description": "Fade duration when tiles load (ms)"
            }
          ]
        },
        {
          "name": "LegendProps",
          "description": "Props for map legend component",
          "properties": [
            {
              "name": "title",
              "type": "string",
              "required": false,
              "description": "Legend title"
            },
            {
              "name": "position",
              "type": "ControlPosition",
              "required": false,
              "description": "Position on map"
            },
            {
              "name": "type",
              "type": "'categorical' | 'gradient'",
              "required": false,
              "description": "Legend type"
            },
            {
              "name": "items",
              "type": "LegendItem[]",
              "required": false,
              "description": "Items for categorical legends"
            },
            {
              "name": "gradient",
              "type": "GradientStop[]",
              "required": false,
              "description": "Gradient stops for continuous legends"
            },
            {
              "name": "minLabel",
              "type": "string",
              "required": false,
              "description": "Min value label for gradient"
            },
            {
              "name": "maxLabel",
              "type": "string",
              "required": false,
              "description": "Max value label for gradient"
            },
            {
              "name": "collapsible",
              "type": "boolean",
              "required": false,
              "description": "Whether legend is collapsible"
            },
            {
              "name": "collapsed",
              "type": "boolean",
              "required": false,
              "description": "Whether initially collapsed"
            },
            {
              "name": "onItemClick",
              "type": "(item: LegendItem, index: number) => void",
              "required": false,
              "description": "Callback for item click"
            }
          ]
        },
        {
          "name": "LegendGroupProps",
          "description": "Props for accordion-style legend container",
          "properties": [
            {
              "name": "legends",
              "type": "LegendConfig[]",
              "required": true,
              "description": "Array of legend configurations"
            },
            {
              "name": "position",
              "type": "ControlPosition",
              "required": false,
              "description": "Position on map"
            },
            {
              "name": "defaultExpanded",
              "type": "string",
              "required": false,
              "description": "ID of initially expanded legend"
            },
            {
              "name": "expanded",
              "type": "string | null",
              "required": false,
              "description": "Controlled expanded state"
            },
            {
              "name": "onExpandedChange",
              "type": "(id: string | null) => void",
              "required": false,
              "description": "Callback when expanded changes"
            },
            {
              "name": "allowCollapseAll",
              "type": "boolean",
              "required": false,
              "description": "Allow collapsing all (default: false)"
            }
          ]
        },
        {
          "name": "CoordinateDisplayProps",
          "description": "Props for coordinate display control",
          "properties": [
            {
              "name": "position",
              "type": "ControlPosition",
              "required": false,
              "description": "Position on map"
            },
            {
              "name": "format",
              "type": "'decimal' | 'dms' | 'dd'",
              "required": false,
              "description": "Coordinate format"
            },
            {
              "name": "precision",
              "type": "number",
              "required": false,
              "description": "Decimal places"
            },
            {
              "name": "showZoom",
              "type": "boolean",
              "required": false,
              "description": "Show zoom level"
            },
            {
              "name": "showBearing",
              "type": "boolean",
              "required": false,
              "description": "Show bearing"
            }
          ]
        },
        {
          "name": "NavigationControlProps",
          "description": "Props for navigation control",
          "properties": [
            {
              "name": "position",
              "type": "ControlPosition",
              "required": false,
              "description": "Position on map"
            },
            {
              "name": "showCompass",
              "type": "boolean",
              "required": false,
              "description": "Show compass"
            },
            {
              "name": "showZoom",
              "type": "boolean",
              "required": false,
              "description": "Show zoom buttons"
            },
            {
              "name": "visualizePitch",
              "type": "boolean",
              "required": false,
              "description": "Visualize pitch with compass"
            }
          ]
        },
        {
          "name": "ScaleControlProps",
          "description": "Props for scale control",
          "properties": [
            {
              "name": "position",
              "type": "ControlPosition",
              "required": false,
              "description": "Position on map"
            },
            {
              "name": "maxWidth",
              "type": "number",
              "required": false,
              "description": "Maximum width of scale"
            },
            {
              "name": "unit",
              "type": "'imperial' | 'metric' | 'nautical'",
              "required": false,
              "description": "Unit system"
            }
          ]
        },
        {
          "name": "GeolocateControlProps",
          "description": "Props for geolocate control",
          "properties": [
            {
              "name": "position",
              "type": "ControlPosition",
              "required": false,
              "description": "Position on map"
            },
            {
              "name": "trackUserLocation",
              "type": "boolean",
              "required": false,
              "description": "Track user location"
            },
            {
              "name": "showAccuracyCircle",
              "type": "boolean",
              "required": false,
              "description": "Show accuracy circle"
            },
            {
              "name": "showUserHeading",
              "type": "boolean",
              "required": false,
              "description": "Show user heading"
            }
          ]
        },
        {
          "name": "BasemapSwitcherProps",
          "description": "Props for the basemap switcher control",
          "properties": [
            {
              "name": "position",
              "type": "ControlPosition",
              "required": false,
              "description": "Position on map (default: 'bottom-right')"
            },
            {
              "name": "basemaps",
              "type": "BasemapDefinition[]",
              "required": false,
              "description": "Override the default basemap list (defaults to FREE_BASEMAPS)"
            },
            {
              "name": "extraBasemaps",
              "type": "BasemapDefinition[]",
              "required": false,
              "description": "Additional basemaps appended to the default list"
            },
            {
              "name": "apiKeys",
              "type": "BasemapApiKeys",
              "required": false,
              "description": "API keys for providers (maptiler, stadia)"
            },
            {
              "name": "defaultBasemapId",
              "type": "string",
              "required": false,
              "description": "ID of the initially active basemap"
            },
            {
              "name": "activeBasemapId",
              "type": "string",
              "required": false,
              "description": "Controlled active basemap ID"
            },
            {
              "name": "onBasemapChange",
              "type": "(basemap: BasemapDefinition) => void",
              "required": false,
              "description": "Callback when basemap changes"
            },
            {
              "name": "columns",
              "type": "number",
              "required": false,
              "description": "Grid columns in flyout (default: 3)"
            },
            {
              "name": "thumbnailSize",
              "type": "number",
              "required": false,
              "description": "Thumbnail size in pixels (default: 64)"
            },
            {
              "name": "class",
              "type": "string",
              "required": false,
              "description": "Additional CSS classes"
            }
          ]
        },
        {
          "name": "CustomControlProps",
          "description": "Props for positioning arbitrary custom content on the map with collision avoidance and z-index management",
          "properties": [
            {
              "name": "position",
              "type": "ExtendedControlPosition",
              "required": false,
              "description": "Position on the map (default: 'top-right')"
            },
            {
              "name": "fallbacks",
              "type": "ExtendedControlPosition[]",
              "required": false,
              "description": "Fallback positions if primary is occupied"
            },
            {
              "name": "priority",
              "type": "ControlPriority",
              "required": false,
              "description": "Priority for collision resolution (default: 'normal')"
            },
            {
              "name": "zLayer",
              "type": "ZIndexLayer",
              "required": false,
              "description": "Z-index layer (default: 'interactive')"
            },
            {
              "name": "id",
              "type": "string",
              "required": false,
              "description": "Unique identifier (auto-generated if not provided)"
            },
            {
              "name": "hasExpandedState",
              "type": "boolean",
              "required": false,
              "description": "Whether the control has an expandable state"
            },
            {
              "name": "className",
              "type": "string",
              "required": false,
              "description": "Additional CSS class names (React)"
            },
            {
              "name": "class",
              "type": "string",
              "required": false,
              "description": "Additional CSS class names (Svelte)"
            },
            {
              "name": "style",
              "type": "CSSProperties | string",
              "required": false,
              "description": "Inline styles (merged with positioning styles)"
            },
            {
              "name": "children",
              "type": "ReactNode | Snippet",
              "required": true,
              "description": "Custom content to display"
            }
          ]
        }
      ]
    },
    "data": {
      "description": "Data structure types",
      "types": [
        {
          "name": "LngLat",
          "description": "Longitude/latitude coordinate pair as [lng, lat] tuple"
        },
        {
          "name": "MapBounds",
          "description": "Geographic bounding box with southwest and northeast corners",
          "properties": [
            {
              "name": "southwest",
              "type": "LngLat",
              "required": true,
              "description": "Southwest corner"
            },
            {
              "name": "northeast",
              "type": "LngLat",
              "required": true,
              "description": "Northeast corner"
            }
          ]
        },
        {
          "name": "BoundsArray",
          "description": "Bounding box as [[sw_lng, sw_lat], [ne_lng, ne_lat]]"
        },
        {
          "name": "MapViewport",
          "description": "Map viewport state (center, zoom, bearing, pitch)"
        },
        {
          "name": "GeoJSONFeature",
          "description": "GeoJSON Feature with typed properties"
        },
        {
          "name": "GeoJSONFeatureCollection",
          "description": "GeoJSON FeatureCollection with typed features"
        },
        {
          "name": "GeoJSONGeometry",
          "description": "Union of all GeoJSON geometry types"
        },
        {
          "name": "MarkerData",
          "description": "Data for map markers",
          "properties": [
            {
              "name": "id",
              "type": "string | number",
              "required": true,
              "description": "Unique identifier"
            },
            {
              "name": "coordinates",
              "type": "LngLat",
              "required": true,
              "description": "Marker position"
            },
            {
              "name": "label",
              "type": "string",
              "required": false,
              "description": "Display label"
            },
            {
              "name": "color",
              "type": "string",
              "required": false,
              "description": "Marker color"
            }
          ]
        },
        {
          "name": "ClusterConfig",
          "description": "Configuration for source clustering",
          "properties": [
            {
              "name": "radius",
              "type": "number",
              "required": false,
              "description": "Cluster radius in pixels"
            },
            {
              "name": "maxZoom",
              "type": "number",
              "required": false,
              "description": "Max zoom for clustering"
            },
            {
              "name": "minPoints",
              "type": "number",
              "required": false,
              "description": "Minimum points to form cluster"
            }
          ]
        },
        {
          "name": "ClusterFeatureProperties",
          "description": "Properties automatically added to cluster features by MapLibre",
          "properties": [
            {
              "name": "cluster_id",
              "type": "number",
              "required": true,
              "description": "Unique cluster identifier"
            },
            {
              "name": "point_count",
              "type": "number",
              "required": true,
              "description": "Number of points in cluster"
            },
            {
              "name": "point_count_abbreviated",
              "type": "string",
              "required": true,
              "description": "Abbreviated count (e.g., '1.2k')"
            },
            {
              "name": "cluster",
              "type": "true",
              "required": true,
              "description": "Whether this is a cluster"
            }
          ]
        },
        {
          "name": "LegendItem",
          "description": "Item for categorical legends",
          "properties": [
            {
              "name": "label",
              "type": "string",
              "required": true,
              "description": "Item label"
            },
            {
              "name": "color",
              "type": "string",
              "required": true,
              "description": "Item color"
            },
            {
              "name": "icon",
              "type": "string",
              "required": false,
              "description": "Optional icon name"
            },
            {
              "name": "value",
              "type": "string | number",
              "required": false,
              "description": "Optional value"
            },
            {
              "name": "active",
              "type": "boolean",
              "required": false,
              "description": "Whether active/visible"
            }
          ]
        },
        {
          "name": "GradientStop",
          "description": "Stop for gradient legends",
          "properties": [
            {
              "name": "position",
              "type": "number",
              "required": true,
              "description": "Position in gradient (0-1)"
            },
            {
              "name": "color",
              "type": "string",
              "required": true,
              "description": "Color at position"
            },
            {
              "name": "label",
              "type": "string",
              "required": false,
              "description": "Optional label"
            }
          ]
        },
        {
          "name": "ControlPosition",
          "description": "Position for map controls: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'"
        },
        {
          "name": "LoadGeoJSONOptions",
          "description": "Options for loadGeoJSON function",
          "properties": [
            {
              "name": "timeout",
              "type": "number",
              "required": false,
              "description": "Request timeout in ms (default: 30000)"
            },
            {
              "name": "fetchOptions",
              "type": "RequestInit",
              "required": false,
              "description": "Additional fetch options"
            },
            {
              "name": "validate",
              "type": "boolean",
              "required": false,
              "description": "Validate structure after loading"
            }
          ]
        },
        {
          "name": "ValidationResult",
          "description": "Result of GeoJSON validation",
          "properties": [
            {
              "name": "valid",
              "type": "boolean",
              "required": true,
              "description": "Whether valid"
            },
            {
              "name": "errors",
              "type": "string[]",
              "required": true,
              "description": "List of errors"
            },
            {
              "name": "featureCount",
              "type": "number",
              "required": false,
              "description": "Number of features"
            },
            {
              "name": "geometryTypes",
              "type": "string[]",
              "required": false,
              "description": "Geometry types found"
            }
          ]
        },
        {
          "name": "ClusterExpandOptions",
          "description": "Options for cluster expansion",
          "properties": [
            {
              "name": "duration",
              "type": "number",
              "required": false,
              "description": "Animation duration in ms (default: 500)"
            },
            {
              "name": "animate",
              "type": "boolean",
              "required": false,
              "description": "Whether to animate (default: true)"
            },
            {
              "name": "padding",
              "type": "number | { top, bottom, left, right }",
              "required": false,
              "description": "Padding around bounds"
            },
            {
              "name": "maxZoom",
              "type": "number",
              "required": false,
              "description": "Maximum zoom level"
            }
          ]
        },
        {
          "name": "GeoJSONFingerprint",
          "description": "Lightweight fingerprint for fast GeoJSON equality checking",
          "properties": [
            {
              "name": "type",
              "type": "string",
              "required": true,
              "description": "GeoJSON type (FeatureCollection, Feature, etc.)"
            },
            {
              "name": "featureCount",
              "type": "number",
              "required": true,
              "description": "Number of features"
            },
            {
              "name": "firstFeatureId",
              "type": "string | number | undefined",
              "required": false,
              "description": "ID of first feature"
            },
            {
              "name": "lastFeatureId",
              "type": "string | number | undefined",
              "required": false,
              "description": "ID of last feature"
            },
            {
              "name": "geometryTypes",
              "type": "Set<string>",
              "required": true,
              "description": "Set of geometry types present"
            }
          ]
        },
        {
          "name": "PaintProps",
          "description": "MapLibre paint properties object (Record<string, unknown>)"
        },
        {
          "name": "PaintPropsDiff",
          "description": "Result of comparing two paint prop objects",
          "properties": [
            {
              "name": "hasChanges",
              "type": "boolean",
              "required": true,
              "description": "Whether any properties changed"
            },
            {
              "name": "changed",
              "type": "Record<string, unknown>",
              "required": true,
              "description": "Properties that changed (key -> new value)"
            },
            {
              "name": "removed",
              "type": "string[]",
              "required": true,
              "description": "Properties that were removed"
            }
          ]
        },
        {
          "name": "FilterExpression",
          "description": "MapLibre filter expression (unknown[] | undefined)"
        },
        {
          "name": "SpatialIndex",
          "description": "Grid-based spatial index for viewport queries",
          "properties": [
            {
              "name": "cells",
              "type": "Map<string, GeoJSONFeature[]>",
              "required": true,
              "description": "Grid cells containing features"
            },
            {
              "name": "bounds",
              "type": "BoundsArray",
              "required": true,
              "description": "Total bounds of indexed data"
            },
            {
              "name": "cellSize",
              "type": "{ lng: number; lat: number }",
              "required": true,
              "description": "Size of each grid cell"
            },
            {
              "name": "featureCount",
              "type": "number",
              "required": true,
              "description": "Total number of features indexed"
            }
          ]
        },
        {
          "name": "RTreeIndex",
          "description": "R-tree spatial index (better for clustered data)",
          "properties": [
            {
              "name": "tree",
              "type": "RBush<RTreeItem>",
              "required": true,
              "description": "R-tree data structure"
            },
            {
              "name": "bounds",
              "type": "BoundsArray",
              "required": true,
              "description": "Total bounds of indexed data"
            },
            {
              "name": "featureCount",
              "type": "number",
              "required": true,
              "description": "Total number of features indexed"
            }
          ]
        },
        {
          "name": "QueryStats",
          "description": "Statistics from a spatial query",
          "properties": [
            {
              "name": "totalFeatures",
              "type": "number",
              "required": true,
              "description": "Total features in index"
            },
            {
              "name": "visibleFeatures",
              "type": "number",
              "required": true,
              "description": "Features in query result"
            },
            {
              "name": "culledPercent",
              "type": "number",
              "required": true,
              "description": "Percentage of features culled"
            },
            {
              "name": "cellsChecked",
              "type": "number",
              "required": true,
              "description": "Number of grid cells checked"
            },
            {
              "name": "totalCells",
              "type": "number",
              "required": true,
              "description": "Total cells in grid"
            }
          ]
        },
        {
          "name": "PanVelocity",
          "description": "Velocity vector for predictive panning",
          "properties": [
            {
              "name": "lngPerSec",
              "type": "number",
              "required": true,
              "description": "Longitude change per second"
            },
            {
              "name": "latPerSec",
              "type": "number",
              "required": true,
              "description": "Latitude change per second"
            },
            {
              "name": "magnitude",
              "type": "number",
              "required": true,
              "description": "Velocity magnitude"
            }
          ]
        },
        {
          "name": "LODData",
          "description": "Level-of-Detail data structure",
          "properties": [
            {
              "name": "levels",
              "type": "Map<number, GeoJSONFeatureCollection>",
              "required": true,
              "description": "Data at each zoom level"
            },
            {
              "name": "zoomThresholds",
              "type": "number[]",
              "required": true,
              "description": "Zoom level breakpoints"
            },
            {
              "name": "originalFeatureCount",
              "type": "number",
              "required": true,
              "description": "Original feature count"
            }
          ]
        },
        {
          "name": "SimplificationStats",
          "description": "Statistics from polygon simplification",
          "properties": [
            {
              "name": "originalPoints",
              "type": "number",
              "required": true,
              "description": "Original point count"
            },
            {
              "name": "simplifiedPoints",
              "type": "number",
              "required": true,
              "description": "Point count after simplification"
            },
            {
              "name": "reductionPercent",
              "type": "number",
              "required": true,
              "description": "Percentage of points removed"
            },
            {
              "name": "processingTimeMs",
              "type": "number",
              "required": true,
              "description": "Time taken in milliseconds"
            }
          ]
        },
        {
          "name": "SpatialWorkerClientOptions",
          "description": "Options for creating a SpatialWorkerClient",
          "properties": [
            {
              "name": "requestTimeout",
              "type": "number",
              "required": false,
              "description": "Timeout for requests in ms (default: 30000)"
            },
            {
              "name": "useRTree",
              "type": "boolean",
              "required": false,
              "description": "Use R-tree (true) or grid index (false). Default: true"
            },
            {
              "name": "gridTargetCells",
              "type": "number",
              "required": false,
              "description": "Target cells for grid index. Default: 1024"
            },
            {
              "name": "rtreeMaxEntries",
              "type": "number",
              "required": false,
              "description": "Max entries per R-tree node. Default: 9"
            },
            {
              "name": "workerFactory",
              "type": "() => Worker",
              "required": false,
              "description": "Factory function to create the worker"
            },
            {
              "name": "workerUrl",
              "type": "string | URL",
              "required": false,
              "description": "URL to the worker script (alternative to workerFactory)"
            }
          ]
        },
        {
          "name": "WorkerQueryResult",
          "description": "Result from a worker viewport query",
          "properties": [
            {
              "name": "success",
              "type": "boolean",
              "required": true,
              "description": "Whether query succeeded"
            },
            {
              "name": "data",
              "type": "GeoJSONFeatureCollection",
              "required": true,
              "description": "Query results"
            },
            {
              "name": "stats",
              "type": "QueryStats",
              "required": true,
              "description": "Query statistics"
            },
            {
              "name": "queryTimeMs",
              "type": "number",
              "required": true,
              "description": "Time taken in milliseconds"
            },
            {
              "name": "error",
              "type": "string",
              "required": false,
              "description": "Error message if failed"
            }
          ]
        }
      ]
    },
    "events": {
      "description": "Event types",
      "types": [
        {
          "name": "BaseMapEventParams",
          "description": "Base event parameters shared across all map events",
          "properties": [
            {
              "name": "type",
              "type": "string",
              "required": true,
              "description": "Event type identifier"
            },
            {
              "name": "originalEvent",
              "type": "MouseEvent | TouchEvent",
              "required": false,
              "description": "Original native event"
            }
          ]
        },
        {
          "name": "MapClickEventParams",
          "description": "Parameters for map click events",
          "properties": [
            {
              "name": "type",
              "type": "'click' | 'dblclick' | 'contextmenu'",
              "required": true,
              "description": "Event type"
            },
            {
              "name": "lngLat",
              "type": "LngLat",
              "required": true,
              "description": "Click location [lng, lat]"
            },
            {
              "name": "point",
              "type": "[number, number]",
              "required": true,
              "description": "Screen pixel coordinates [x, y]"
            },
            {
              "name": "features",
              "type": "GeoJSONFeature[]",
              "required": false,
              "description": "Features at click point"
            }
          ]
        },
        {
          "name": "MapHoverEventParams",
          "description": "Parameters for mouse move/hover events",
          "properties": [
            {
              "name": "type",
              "type": "'mousemove' | 'mouseenter' | 'mouseleave'",
              "required": true,
              "description": "Event type"
            },
            {
              "name": "lngLat",
              "type": "LngLat",
              "required": true,
              "description": "Cursor location [lng, lat]"
            },
            {
              "name": "point",
              "type": "[number, number]",
              "required": true,
              "description": "Screen pixel coordinates [x, y]"
            },
            {
              "name": "features",
              "type": "GeoJSONFeature[]",
              "required": false,
              "description": "Features at cursor location"
            }
          ]
        },
        {
          "name": "FeatureClickEventParams",
          "description": "Parameters for feature click events",
          "properties": [
            {
              "name": "type",
              "type": "'click' | 'dblclick' | 'contextmenu'",
              "required": true,
              "description": "Event type"
            },
            {
              "name": "feature",
              "type": "GeoJSONFeature",
              "required": true,
              "description": "Clicked feature"
            },
            {
              "name": "featureId",
              "type": "string | number",
              "required": false,
              "description": "Feature ID"
            },
            {
              "name": "layerId",
              "type": "string",
              "required": true,
              "description": "Layer ID"
            },
            {
              "name": "sourceId",
              "type": "string",
              "required": true,
              "description": "Source ID"
            },
            {
              "name": "lngLat",
              "type": "LngLat",
              "required": true,
              "description": "Click location"
            },
            {
              "name": "point",
              "type": "[number, number]",
              "required": true,
              "description": "Screen coordinates"
            }
          ]
        },
        {
          "name": "FeatureHoverEventParams",
          "description": "Parameters for feature hover events",
          "properties": [
            {
              "name": "type",
              "type": "'mouseenter' | 'mousemove' | 'mouseleave'",
              "required": true,
              "description": "Event type"
            },
            {
              "name": "feature",
              "type": "GeoJSONFeature | null",
              "required": true,
              "description": "Hovered feature (null on leave)"
            },
            {
              "name": "featureId",
              "type": "string | number",
              "required": false,
              "description": "Feature ID"
            },
            {
              "name": "layerId",
              "type": "string",
              "required": true,
              "description": "Layer ID"
            },
            {
              "name": "sourceId",
              "type": "string",
              "required": true,
              "description": "Source ID"
            },
            {
              "name": "lngLat",
              "type": "LngLat",
              "required": true,
              "description": "Cursor location"
            }
          ]
        },
        {
          "name": "MarkerClickEventParams",
          "description": "Parameters for marker click events",
          "properties": [
            {
              "name": "type",
              "type": "'click'",
              "required": true,
              "description": "Event type"
            },
            {
              "name": "markerId",
              "type": "string",
              "required": true,
              "description": "Marker ID"
            },
            {
              "name": "lngLat",
              "type": "LngLat",
              "required": true,
              "description": "Marker coordinates"
            },
            {
              "name": "properties",
              "type": "Record<string, unknown>",
              "required": false,
              "description": "Marker properties"
            }
          ]
        },
        {
          "name": "MarkerDragEventParams",
          "description": "Parameters for marker drag events",
          "properties": [
            {
              "name": "type",
              "type": "'dragstart' | 'drag' | 'dragend'",
              "required": true,
              "description": "Event type"
            },
            {
              "name": "markerId",
              "type": "string",
              "required": true,
              "description": "Marker ID"
            },
            {
              "name": "lngLat",
              "type": "LngLat",
              "required": true,
              "description": "Current coordinates"
            },
            {
              "name": "originalLngLat",
              "type": "LngLat",
              "required": false,
              "description": "Original coordinates at drag start"
            }
          ]
        },
        {
          "name": "MapLoadEventParams",
          "description": "Parameters for map load events",
          "properties": [
            {
              "name": "type",
              "type": "'load' | 'style.load' | 'idle'",
              "required": true,
              "description": "Event type"
            },
            {
              "name": "loaded",
              "type": "boolean",
              "required": true,
              "description": "Whether fully loaded"
            }
          ]
        },
        {
          "name": "MapMoveEventParams",
          "description": "Parameters for map move events",
          "properties": [
            {
              "name": "type",
              "type": "'move' | 'movestart' | 'moveend' | 'zoom' | ...",
              "required": true,
              "description": "Event type"
            },
            {
              "name": "center",
              "type": "LngLat",
              "required": true,
              "description": "New center"
            },
            {
              "name": "zoom",
              "type": "number",
              "required": true,
              "description": "New zoom level"
            },
            {
              "name": "bearing",
              "type": "number",
              "required": true,
              "description": "New bearing"
            },
            {
              "name": "pitch",
              "type": "number",
              "required": true,
              "description": "New pitch"
            },
            {
              "name": "isUserInteraction",
              "type": "boolean",
              "required": false,
              "description": "Whether user-initiated"
            }
          ]
        },
        {
          "name": "PopupEventParams",
          "description": "Parameters for popup events",
          "properties": [
            {
              "name": "type",
              "type": "'open' | 'close'",
              "required": true,
              "description": "Event type"
            },
            {
              "name": "lngLat",
              "type": "LngLat",
              "required": true,
              "description": "Popup coordinates"
            }
          ]
        },
        {
          "name": "MapErrorEventParams",
          "description": "Parameters for map error events",
          "properties": [
            {
              "name": "type",
              "type": "'error'",
              "required": true,
              "description": "Event type"
            },
            {
              "name": "message",
              "type": "string",
              "required": true,
              "description": "Error message"
            },
            {
              "name": "source",
              "type": "string",
              "required": false,
              "description": "Error source (tile, source, layer)"
            },
            {
              "name": "error",
              "type": "Error",
              "required": false,
              "description": "Original error object"
            }
          ]
        }
      ]
    },
    "basemaps": {
      "description": "Basemap-related types for the basemap switching system",
      "types": [
        {
          "name": "BasemapProvider",
          "description": "Supported basemap tile providers",
          "type": "'osm' | 'maptiler' | 'stadia' | 'classic-theme' | 'custom'"
        },
        {
          "name": "BasemapCategory",
          "description": "Basemap visual categories",
          "type": "'streets' | 'satellite' | 'outdoor' | 'artistic' | 'minimal' | 'dark'"
        },
        {
          "name": "BasemapDefinition",
          "description": "Full basemap definition including metadata and style resolution",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Unique basemap identifier"
            },
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Display name"
            },
            {
              "name": "provider",
              "type": "BasemapProvider",
              "required": true,
              "description": "Tile provider"
            },
            {
              "name": "category",
              "type": "BasemapCategory",
              "required": true,
              "description": "Visual category"
            },
            {
              "name": "thumbnail",
              "type": "string",
              "required": true,
              "description": "Thumbnail URL (may contain {key} placeholder)"
            },
            {
              "name": "style",
              "type": "string | StyleSpecification",
              "required": true,
              "description": "MapLibre style URL or spec (may contain {key} placeholder)"
            },
            {
              "name": "requiresApiKey",
              "type": "boolean",
              "required": true,
              "description": "Whether an API key is needed"
            },
            {
              "name": "attribution",
              "type": "string",
              "required": false,
              "description": "Custom attribution text"
            },
            {
              "name": "label",
              "type": "string",
              "required": false,
              "description": "Short label for UI (defaults to name)"
            }
          ]
        },
        {
          "name": "BasemapApiKeys",
          "description": "API keys for basemap providers",
          "properties": [
            {
              "name": "maptiler",
              "type": "string",
              "required": false,
              "description": "MapTiler API key"
            },
            {
              "name": "stadia",
              "type": "string",
              "required": false,
              "description": "Stadia Maps API key"
            },
            {
              "name": "[provider: string]",
              "type": "string",
              "required": false,
              "description": "Custom provider API key (index signature)"
            }
          ]
        }
      ]
    },
    "overlayLayers": {
      "description": "Overlay layer system for the LayerManager control - adds GIS overlay layers (school districts, parks, flood zones, etc.) on top of basemaps",
      "types": [
        {
          "name": "OverlayLayerCategory",
          "description": "Categories for grouping overlay layers",
          "type": "'boundaries' | 'recreation' | 'transportation' | 'environmental' | 'custom'"
        },
        {
          "name": "OverlayLayerCategoryMeta",
          "description": "Metadata for layer categories",
          "properties": [
            {
              "name": "label",
              "type": "string",
              "required": true,
              "description": "Display label for the category"
            },
            {
              "name": "icon",
              "type": "string",
              "required": true,
              "description": "Icon identifier for the category"
            },
            {
              "name": "order",
              "type": "number",
              "required": true,
              "description": "Order priority for display (lower numbers first)"
            }
          ]
        },
        {
          "name": "OverlayDataSource",
          "description": "Data source configuration for overlay layers",
          "properties": [
            {
              "name": "type",
              "type": "'geojson-url' | 'geojson-inline' | 'vector-tiles'",
              "required": true,
              "description": "Type of data source"
            },
            {
              "name": "url",
              "type": "string",
              "required": false,
              "description": "URL for remote data sources (may contain {apiKey} placeholder)"
            },
            {
              "name": "data",
              "type": "GeoJSONFeatureCollection",
              "required": false,
              "description": "Inline GeoJSON data (for geojson-inline type)"
            },
            {
              "name": "sourceLayer",
              "type": "string",
              "required": false,
              "description": "Source layer name (for vector tiles)"
            },
            {
              "name": "attribution",
              "type": "string",
              "required": false,
              "description": "Attribution text for the data source"
            }
          ]
        },
        {
          "name": "OverlayLayerStyle",
          "description": "Theme-aware styling configuration for overlay layers",
          "properties": [
            {
              "name": "renderType",
              "type": "'fill' | 'line' | 'circle' | 'symbol'",
              "required": true,
              "description": "MapLibre render type for the layer"
            },
            {
              "name": "light",
              "type": "Record<string, unknown>",
              "required": true,
              "description": "Paint properties for light theme"
            },
            {
              "name": "dark",
              "type": "Record<string, unknown>",
              "required": true,
              "description": "Paint properties for dark theme"
            },
            {
              "name": "layout",
              "type": "Record<string, unknown>",
              "required": false,
              "description": "Layout properties (shared between themes)"
            },
            {
              "name": "minZoom",
              "type": "number",
              "required": false,
              "description": "Minimum zoom level for the layer"
            },
            {
              "name": "maxZoom",
              "type": "number",
              "required": false,
              "description": "Maximum zoom level for the layer"
            }
          ]
        },
        {
          "name": "OverlayLegendItem",
          "description": "Legend item for categorical layers",
          "properties": [
            {
              "name": "label",
              "type": "string",
              "required": true,
              "description": "Display label"
            },
            {
              "name": "color",
              "type": "string",
              "required": true,
              "description": "Color value"
            },
            {
              "name": "icon",
              "type": "string",
              "required": false,
              "description": "Optional icon name"
            }
          ]
        },
        {
          "name": "OverlayLayerLegend",
          "description": "Legend configuration for overlay layers",
          "properties": [
            {
              "name": "type",
              "type": "'categorical' | 'gradient'",
              "required": true,
              "description": "Legend type"
            },
            {
              "name": "items",
              "type": "OverlayLegendItem[]",
              "required": false,
              "description": "Items for categorical legends"
            },
            {
              "name": "gradient",
              "type": "{ position: number; color: string; label?: string }[]",
              "required": false,
              "description": "Gradient stops for continuous legends"
            },
            {
              "name": "minLabel",
              "type": "string",
              "required": false,
              "description": "Min label for gradient"
            },
            {
              "name": "maxLabel",
              "type": "string",
              "required": false,
              "description": "Max label for gradient"
            }
          ]
        },
        {
          "name": "OverlayLayerDefinition",
          "description": "Complete overlay layer definition with source, styling, and metadata",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Unique identifier for the layer"
            },
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Display name for the layer"
            },
            {
              "name": "description",
              "type": "string",
              "required": false,
              "description": "Description of the layer"
            },
            {
              "name": "category",
              "type": "OverlayLayerCategory",
              "required": true,
              "description": "Layer category for grouping"
            },
            {
              "name": "source",
              "type": "OverlayDataSource",
              "required": true,
              "description": "Data source configuration"
            },
            {
              "name": "style",
              "type": "OverlayLayerStyle",
              "required": true,
              "description": "Theme-aware styling"
            },
            {
              "name": "legend",
              "type": "OverlayLayerLegend",
              "required": false,
              "description": "Legend configuration"
            },
            {
              "name": "interactive",
              "type": "boolean",
              "required": false,
              "description": "Whether the layer supports feature interaction (click/hover)"
            },
            {
              "name": "labelProperty",
              "type": "string",
              "required": false,
              "description": "Property name to use for feature labels"
            },
            {
              "name": "tags",
              "type": "string[]",
              "required": false,
              "description": "Tags for filtering/searching"
            }
          ]
        },
        {
          "name": "OverlayLayerState",
          "description": "Runtime state for an overlay layer",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Layer ID"
            },
            {
              "name": "visible",
              "type": "boolean",
              "required": true,
              "description": "Whether the layer is visible"
            },
            {
              "name": "opacity",
              "type": "number",
              "required": true,
              "description": "Layer opacity (0-1)"
            },
            {
              "name": "loading",
              "type": "boolean",
              "required": true,
              "description": "Whether the layer is currently loading"
            },
            {
              "name": "error",
              "type": "string | null",
              "required": true,
              "description": "Error message if loading failed"
            },
            {
              "name": "order",
              "type": "number",
              "required": true,
              "description": "Display order (lower numbers render first/bottom)"
            }
          ]
        },
        {
          "name": "OverlayApiKeys",
          "description": "API keys that may be required for layer data sources",
          "properties": [
            {
              "name": "[provider: string]",
              "type": "string | undefined",
              "required": false,
              "description": "API key by layer ID or 'default'"
            }
          ]
        },
        {
          "name": "LayerManagerProps",
          "description": "Props for the LayerManager component",
          "properties": [
            {
              "name": "layers",
              "type": "OverlayLayerDefinition[]",
              "required": true,
              "description": "Layer definitions to display"
            },
            {
              "name": "apiKeys",
              "type": "OverlayApiKeys",
              "required": false,
              "description": "API keys for data sources that require authentication"
            },
            {
              "name": "initialActiveLayers",
              "type": "string[]",
              "required": false,
              "description": "IDs of layers that should be active initially"
            },
            {
              "name": "position",
              "type": "'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'",
              "required": false,
              "description": "Control position on the map (default: 'top-right')"
            },
            {
              "name": "showSearch",
              "type": "boolean",
              "required": false,
              "description": "Show search input for filtering layers"
            },
            {
              "name": "showCategories",
              "type": "boolean",
              "required": false,
              "description": "Show category tabs for grouping layers"
            },
            {
              "name": "allowReorder",
              "type": "boolean",
              "required": false,
              "description": "Allow drag-to-reorder layers"
            },
            {
              "name": "showOpacity",
              "type": "boolean",
              "required": false,
              "description": "Show opacity sliders for each layer"
            },
            {
              "name": "collapsible",
              "type": "boolean",
              "required": false,
              "description": "Allow collapsing the panel (default: true)"
            },
            {
              "name": "onLayerChange",
              "type": "(layerId: string, state: OverlayLayerState) => void",
              "required": false,
              "description": "Callback when a layer state changes"
            },
            {
              "name": "class",
              "type": "string",
              "required": false,
              "description": "Custom CSS class"
            }
          ]
        }
      ],
      "utilities": {
        "registry": {
          "description": "Layer registry utilities and built-in presets",
          "constants": [
            {
              "name": "CATEGORY_META",
              "signature": "Record<OverlayLayerCategory, OverlayLayerCategoryMeta>",
              "description": "Metadata for each layer category (boundaries, recreation, transportation, environmental, custom)"
            },
            {
              "name": "BUILTIN_OVERLAY_LAYERS",
              "signature": "OverlayLayerDefinition[]",
              "description": "Array of all 10 built-in layer presets"
            }
          ],
          "presets": [
            {
              "name": "SCHOOL_DISTRICTS",
              "category": "boundaries",
              "description": "Public school district boundaries (fill layer)"
            },
            {
              "name": "CITY_LIMITS",
              "category": "boundaries",
              "description": "Municipal boundaries (line layer)"
            },
            {
              "name": "ZONING_DISTRICTS",
              "category": "boundaries",
              "description": "Land use zoning designations with categorical legend (fill layer)"
            },
            {
              "name": "PARKS",
              "category": "recreation",
              "description": "Public parks and green spaces (fill layer)"
            },
            {
              "name": "TRAILS",
              "category": "recreation",
              "description": "Hiking, biking, and walking trails (line layer)"
            },
            {
              "name": "SPORTS_FACILITIES",
              "category": "recreation",
              "description": "Sports fields, courts, and facilities (circle layer)"
            },
            {
              "name": "TRANSIT_ROUTES",
              "category": "transportation",
              "description": "Public transportation routes (line layer)"
            },
            {
              "name": "BIKE_LANES",
              "category": "transportation",
              "description": "Designated bicycle lanes and paths (line layer)"
            },
            {
              "name": "FLOOD_ZONES",
              "category": "environmental",
              "description": "FEMA flood hazard areas with categorical legend (fill layer)"
            },
            {
              "name": "WATERSHEDS",
              "category": "environmental",
              "description": "Hydrological watershed boundaries (line layer)"
            }
          ],
          "functions": [
            {
              "name": "resolveLayerSourceUrl",
              "signature": "(layer: OverlayLayerDefinition, apiKeys?: OverlayApiKeys) => string | null",
              "description": "Resolve a layer's data source URL, injecting API key if {apiKey} placeholder is present"
            },
            {
              "name": "getLayerStyleForTheme",
              "signature": "(layer: OverlayLayerDefinition, theme: 'light' | 'dark') => Record<string, unknown>",
              "description": "Get the appropriate paint style for the current theme"
            },
            {
              "name": "searchLayers",
              "signature": "(layers: OverlayLayerDefinition[], query: string) => OverlayLayerDefinition[]",
              "description": "Search layers by name, description, or tags"
            },
            {
              "name": "getLayersByCategory",
              "signature": "(layers: OverlayLayerDefinition[], category: OverlayLayerCategory) => OverlayLayerDefinition[]",
              "description": "Get layers filtered by category"
            },
            {
              "name": "groupLayersByCategory",
              "signature": "(layers: OverlayLayerDefinition[]) => Record<OverlayLayerCategory, OverlayLayerDefinition[]>",
              "description": "Group layers by category"
            },
            {
              "name": "getLayerCategories",
              "signature": "(layers: OverlayLayerDefinition[]) => OverlayLayerCategory[]",
              "description": "Get unique categories present in a set of layers, sorted by order"
            }
          ]
        },
        "state": {
          "description": "Pure functional state management utilities for overlay layers (uses Map<string, OverlayLayerState>)",
          "functions": [
            {
              "name": "createInitialLayerStates",
              "signature": "(layers: OverlayLayerDefinition[], initialActiveLayers?: string[]) => Map<string, OverlayLayerState>",
              "description": "Create initial layer states from definitions"
            },
            {
              "name": "getVisibleLayerIds",
              "signature": "(states: Map<string, OverlayLayerState>) => string[]",
              "description": "Get visible layer IDs in render order (ascending by order)"
            },
            {
              "name": "updateLayerState",
              "signature": "(states: Map<string, OverlayLayerState>, layerId: string, updates: Partial<OverlayLayerState>) => Map<string, OverlayLayerState>",
              "description": "Update a single layer's state"
            },
            {
              "name": "toggleLayerVisibility",
              "signature": "(states: Map<string, OverlayLayerState>, layerId: string) => Map<string, OverlayLayerState>",
              "description": "Toggle layer visibility"
            },
            {
              "name": "setLayerOpacity",
              "signature": "(states: Map<string, OverlayLayerState>, layerId: string, opacity: number) => Map<string, OverlayLayerState>",
              "description": "Set layer opacity (clamped to 0-1)"
            },
            {
              "name": "reorderLayers",
              "signature": "(states: Map<string, OverlayLayerState>, layerId: string, newIndex: number) => Map<string, OverlayLayerState>",
              "description": "Reorder layers by moving a layer to a new position"
            },
            {
              "name": "setLayerLoading",
              "signature": "(states: Map<string, OverlayLayerState>, layerId: string, loading: boolean) => Map<string, OverlayLayerState>",
              "description": "Set loading state for a layer"
            },
            {
              "name": "setLayerError",
              "signature": "(states: Map<string, OverlayLayerState>, layerId: string, error: string | null) => Map<string, OverlayLayerState>",
              "description": "Set error state for a layer"
            },
            {
              "name": "showAllLayers",
              "signature": "(states: Map<string, OverlayLayerState>) => Map<string, OverlayLayerState>",
              "description": "Show all layers"
            },
            {
              "name": "hideAllLayers",
              "signature": "(states: Map<string, OverlayLayerState>) => Map<string, OverlayLayerState>",
              "description": "Hide all layers"
            },
            {
              "name": "getLayerStatesArray",
              "signature": "(states: Map<string, OverlayLayerState>) => OverlayLayerState[]",
              "description": "Get layer states as an array sorted by order"
            },
            {
              "name": "isAnyLayerLoading",
              "signature": "(states: Map<string, OverlayLayerState>) => boolean",
              "description": "Check if any layers are loading"
            },
            {
              "name": "getLayersWithErrors",
              "signature": "(states: Map<string, OverlayLayerState>) => OverlayLayerState[]",
              "description": "Get layers with errors"
            }
          ]
        }
      },
      "cssImport": "@import '@classic-homes/maps-core/styles/layer-manager.css';",
      "example": "import { LayerManager, PARKS, FLOOD_ZONES, SCHOOL_DISTRICTS } from '@classic-homes/maps-svelte';\nimport type { OverlayLayerDefinition } from '@classic-homes/maps-core';\n\n// Configure layers with your data URLs\nconst layers: OverlayLayerDefinition[] = [\n  { ...PARKS, source: { ...PARKS.source, url: '/api/parks.geojson' } },\n  { ...FLOOD_ZONES, source: { ...FLOOD_ZONES.source, url: '/api/flood-zones.geojson' } },\n  { ...SCHOOL_DISTRICTS, source: { ...SCHOOL_DISTRICTS.source, url: '/api/school-districts.geojson' } },\n];\n\n<Map ...>\n  <LayerManager\n    layers={layers}\n    initialActiveLayers={['parks']}\n    showSearch\n    showCategories\n    showOpacity\n    allowReorder\n    position=\"top-right\"\n  />\n</Map>"
    },
    "positioning": {
      "description": "Types for the centralized positioning coordinator system",
      "types": [
        {
          "name": "ExtendedControlPosition",
          "description": "Extended position type supporting 8 positions. Union: 'top-left' | 'top-center' | 'top-right' | 'middle-left' | 'middle-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'"
        },
        {
          "name": "ControlPriority",
          "description": "Priority levels for controls in collision resolution. Union: 'critical' | 'high' | 'normal' | 'low'"
        },
        {
          "name": "ZIndexLayer",
          "description": "Z-index layer types for proper control layering. Union: 'base' | 'interactive' | 'expanded' | 'modal' | 'tooltip'"
        },
        {
          "name": "ControlType",
          "description": "Control type identifiers. Union: 'legend' | 'legend-group' | 'coordinate-display' | 'basemap-switcher' | 'layer-manager' | 'navigation' | 'scale' | 'custom'"
        },
        {
          "name": "ControlDimensions",
          "description": "Dimensions of a control element",
          "properties": [
            {
              "name": "width",
              "type": "number",
              "required": true,
              "description": "Width in pixels"
            },
            {
              "name": "height",
              "type": "number",
              "required": true,
              "description": "Height in pixels"
            }
          ]
        },
        {
          "name": "PositionConfig",
          "description": "Position configuration with fallback support",
          "properties": [
            {
              "name": "primary",
              "type": "ExtendedControlPosition",
              "required": true,
              "description": "Primary position for the control"
            },
            {
              "name": "fallbacks",
              "type": "ExtendedControlPosition[]",
              "required": false,
              "description": "Fallback positions to try if primary is occupied or overflowing"
            }
          ]
        },
        {
          "name": "ControlRegistration",
          "description": "Registration data for a control with the coordinator",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Unique identifier for the control instance"
            },
            {
              "name": "type",
              "type": "ControlType",
              "required": true,
              "description": "Type of control"
            },
            {
              "name": "isBuiltIn",
              "type": "boolean",
              "required": true,
              "description": "Whether this is a built-in MapLibre control"
            },
            {
              "name": "dimensions",
              "type": "ControlDimensions",
              "required": true,
              "description": "Current dimensions of the control"
            },
            {
              "name": "position",
              "type": "PositionConfig",
              "required": true,
              "description": "Position configuration"
            },
            {
              "name": "priority",
              "type": "ControlPriority",
              "required": true,
              "description": "Priority level for collision resolution"
            },
            {
              "name": "zLayer",
              "type": "ZIndexLayer",
              "required": true,
              "description": "Z-index layer for proper layering"
            },
            {
              "name": "hasExpandedState",
              "type": "boolean",
              "required": false,
              "description": "Whether the control has an expanded state"
            },
            {
              "name": "isExpanded",
              "type": "boolean",
              "required": false,
              "description": "Current expanded state"
            },
            {
              "name": "expandedDimensions",
              "type": "ControlDimensions",
              "required": false,
              "description": "Expanded dimensions (when different from collapsed)"
            }
          ]
        },
        {
          "name": "ControlOffset",
          "description": "Computed offset and position for a control",
          "properties": [
            {
              "name": "x",
              "type": "number",
              "required": true,
              "description": "Horizontal offset in pixels from the position anchor"
            },
            {
              "name": "y",
              "type": "number",
              "required": true,
              "description": "Vertical offset in pixels from the position anchor"
            },
            {
              "name": "zIndex",
              "type": "number",
              "required": true,
              "description": "Computed z-index for the control"
            },
            {
              "name": "position",
              "type": "ExtendedControlPosition",
              "required": true,
              "description": "Final resolved position (may differ from requested if fallback was used)"
            },
            {
              "name": "visible",
              "type": "boolean",
              "required": true,
              "description": "Whether the control should be visible"
            },
            {
              "name": "usedFallback",
              "type": "boolean",
              "required": true,
              "description": "Whether a fallback position was used"
            },
            {
              "name": "warning",
              "type": "string",
              "required": false,
              "description": "Warning message if there were positioning issues"
            }
          ]
        },
        {
          "name": "ControlBoundingBox",
          "description": "Internal representation of a control's bounding box",
          "properties": [
            {
              "name": "left",
              "type": "number",
              "required": true,
              "description": "Left edge position"
            },
            {
              "name": "top",
              "type": "number",
              "required": true,
              "description": "Top edge position"
            },
            {
              "name": "right",
              "type": "number",
              "required": true,
              "description": "Right edge position"
            },
            {
              "name": "bottom",
              "type": "number",
              "required": true,
              "description": "Bottom edge position"
            }
          ]
        },
        {
          "name": "MapDimensions",
          "description": "Map dimensions for layout calculations",
          "properties": [
            {
              "name": "width",
              "type": "number",
              "required": true,
              "description": "Map width in pixels"
            },
            {
              "name": "height",
              "type": "number",
              "required": true,
              "description": "Map height in pixels"
            }
          ]
        },
        {
          "name": "CoordinatorEventType",
          "description": "Coordinator event types. Union: 'layout-changed' | 'control-registered' | 'control-unregistered' | 'control-updated' | 'map-resized'"
        },
        {
          "name": "CoordinatorEvent",
          "description": "Event data for coordinator events",
          "properties": [
            {
              "name": "type",
              "type": "CoordinatorEventType",
              "required": true,
              "description": "Type of event"
            },
            {
              "name": "controlId",
              "type": "string",
              "required": false,
              "description": "ID of the affected control (if applicable)"
            },
            {
              "name": "offsets",
              "type": "Map<string, ControlOffset>",
              "required": true,
              "description": "All current control offsets"
            },
            {
              "name": "timestamp",
              "type": "number",
              "required": true,
              "description": "Timestamp of the event"
            }
          ]
        },
        {
          "name": "CoordinatorListener",
          "description": "Listener function type: (event: CoordinatorEvent) => void"
        },
        {
          "name": "ControlUpdateOptions",
          "description": "Options for updating control registration. Partial<Omit<ControlRegistration, 'id' | 'type' | 'isBuiltIn'>>"
        },
        {
          "name": "UseControlPositionOptions",
          "description": "Options for useControlPosition hook/composable",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Unique identifier for the control instance"
            },
            {
              "name": "type",
              "type": "ControlType",
              "required": true,
              "description": "Type of control"
            },
            {
              "name": "position",
              "type": "ExtendedControlPosition",
              "required": true,
              "description": "Primary position for the control"
            },
            {
              "name": "fallbacks",
              "type": "ExtendedControlPosition[]",
              "required": false,
              "description": "Fallback positions to try if primary is occupied"
            },
            {
              "name": "priority",
              "type": "ControlPriority",
              "required": false,
              "description": "Priority level for collision resolution (default: 'normal')"
            },
            {
              "name": "zLayer",
              "type": "ZIndexLayer",
              "required": false,
              "description": "Z-index layer (default: 'interactive')"
            },
            {
              "name": "hasExpandedState",
              "type": "boolean",
              "required": false,
              "description": "Whether the control has an expanded state"
            },
            {
              "name": "isBuiltIn",
              "type": "boolean",
              "required": false,
              "description": "Whether the control is a built-in MapLibre control"
            }
          ]
        },
        {
          "name": "UseControlPositionResult",
          "description": "Return value from useControlPosition hook/composable",
          "properties": [
            {
              "name": "offset",
              "type": "ControlOffset | null",
              "required": true,
              "description": "Current computed offset and position"
            },
            {
              "name": "setExpanded",
              "type": "(isExpanded: boolean) => void",
              "required": true,
              "description": "Update the expanded state"
            },
            {
              "name": "style",
              "type": "React.CSSProperties | string",
              "required": true,
              "description": "Style object (React) or CSS string (Svelte) to apply to the control"
            },
            {
              "name": "visible",
              "type": "boolean",
              "required": true,
              "description": "Whether the control should be visible"
            },
            {
              "name": "hasCoordinator",
              "type": "boolean",
              "required": false,
              "description": "Whether the coordinator is available (Svelte only)"
            }
          ]
        }
      ]
    }
  },
  "components": {
    "react": {
      "package": "@classic-homes/maps-react",
      "components": [
        "Map",
        "MapContext",
        "MapContainer",
        "MapSkeleton",
        "MapError",
        "MapEmpty",
        "Source",
        "Marker",
        "Popup",
        "PopupCard",
        "PopupHeader",
        "PopupContent",
        "PopupFooter",
        "FillLayer",
        "LineLayer",
        "CircleLayer",
        "SymbolLayer",
        "FillExtrusionLayer",
        "HeatmapLayer",
        "Legend",
        "LegendGroup",
        "CoordinateDisplay",
        "BasemapSwitcher",
        "LayerManager",
        "CustomControl",
        "DeckOverlay"
      ],
      "hooks": [
        {
          "name": "useMap",
          "signature": "() => MapContextValue",
          "description": "Hook to access the MapLibre map instance and state from context. Must be used within a Map component.",
          "returns": "{ map: MapLibreMap | null, loaded: boolean, styleVersion: number, setMapStyle: (style: string | StyleSpecification) => void, positioningCoordinator: PositioningCoordinator }"
        },
        {
          "name": "useMapInstance",
          "signature": "() => MapLibreMap | null",
          "description": "Hook to access the map instance only when loaded. Returns null if map is not yet available."
        },
        {
          "name": "useMapTheme",
          "signature": "(theme?: 'light' | 'dark' | 'auto') => { resolvedTheme: 'light' | 'dark', mapTheme: MapTheme, mapStyle: StyleSpecification }",
          "description": "Hook to get the resolved theme based on preference and system settings. Handles 'auto' mode by detecting system dark mode."
        },
        {
          "name": "useReducedMotion",
          "signature": "() => boolean",
          "description": "Hook to detect if user prefers reduced motion. Returns true if prefers-reduced-motion media query matches."
        },
        {
          "name": "useLayer",
          "signature": "(options: UseLayerOptions) => void",
          "description": "Hook for adding custom layers to the map. Handles layer lifecycle (add/update/remove) automatically."
        },
        {
          "name": "useControlPosition",
          "signature": "(ref: RefObject<HTMLElement | null>, options: UseControlPositionOptions) => UseControlPositionResult",
          "description": "Hook for registering controls with the positioning coordinator. Automatically handles dimensions via ResizeObserver, registration lifecycle, and reactive position updates.",
          "returns": "{ offset: ControlOffset | null, setExpanded: (isExpanded: boolean) => void, style: React.CSSProperties, visible: boolean }"
        },
        {
          "name": "useCustomControl",
          "signature": "() => CustomControlContextValue",
          "description": "Hook to access the CustomControl context from child components. Provides setExpanded method to control expanded state.",
          "returns": "{ setExpanded: (isExpanded: boolean) => void }"
        }
      ],
      "context": {
        "name": "MapContext",
        "description": "React context for sharing map instance with child components",
        "value": {
          "map": "MapLibreMap | null - The MapLibre map instance",
          "loaded": "boolean - Whether the map has loaded",
          "styleVersion": "number - Counter incremented on each style.load event (for basemap switching)",
          "setMapStyle": "(style: string | StyleSpecification) => void - Programmatically change the map style",
          "positioningCoordinator": "PositioningCoordinator - Centralized positioning coordinator for map controls"
        }
      }
    },
    "svelte": {
      "package": "@classic-homes/maps-svelte",
      "components": [
        "Map",
        "MapContainer",
        "MapSkeleton",
        "MapError",
        "MapEmpty",
        "Source",
        "Marker",
        "Popup",
        "PopupCard",
        "PopupHeader",
        "PopupContent",
        "PopupFooter",
        "FillLayer",
        "LineLayer",
        "CircleLayer",
        "SymbolLayer",
        "FillExtrusionLayer",
        "HeatmapLayer",
        "Legend",
        "LegendGroup",
        "CoordinateDisplay",
        "BasemapSwitcher",
        "LayerManager",
        "CustomControl",
        "DeckOverlay"
      ],
      "composables": [
        {
          "name": "useMapContext",
          "signature": "() => { map: Writable<Map | null>, loaded: Writable<boolean>, styleVersion: number, setMapStyle: (style: string | StyleSpecification) => void, positioningCoordinator: PositioningCoordinator }",
          "description": "Get the map context from the parent Map component using Svelte context API. Includes styleVersion counter, setMapStyle for basemap switching, and positioningCoordinator for control positioning."
        },
        {
          "name": "tryUseMapContext",
          "signature": "() => MapContextValue | undefined",
          "description": "Safe access to map context that returns undefined instead of throwing if used outside a Map component. Useful for optional map features."
        },
        {
          "name": "useMapTheme",
          "signature": "(theme?: 'light' | 'dark' | 'auto') => { resolvedTheme: 'light' | 'dark', mapTheme: MapTheme, mapStyle: StyleSpecification }",
          "description": "Composable to get the resolved theme based on preference and system settings"
        },
        {
          "name": "useReducedMotion",
          "signature": "() => boolean",
          "description": "Composable to detect if user prefers reduced motion"
        },
        {
          "name": "useLayer",
          "signature": "(options: UseLayerOptions) => void",
          "description": "Composable for adding custom layers to the map with automatic lifecycle management"
        },
        {
          "name": "useControlPosition",
          "signature": "(getElement: () => HTMLElement | undefined, options: UseControlPositionOptions) => UseControlPositionResult",
          "description": "Composable for registering controls with the positioning coordinator. Uses Svelte 5 reactivity for automatic dimension tracking and position updates.",
          "returns": "{ readonly offset: ControlOffset | null, setExpanded: (isExpanded: boolean) => void, readonly style: string, readonly visible: boolean, readonly hasCoordinator: boolean }"
        }
      ]
    }
  },
  "quickReference": {
    "installation": "npm install @classic-homes/maps-svelte maplibre-gl\n# or\nnpm install @classic-homes/maps-react maplibre-gl",
    "basicUsage": "import { Map, Source, CircleLayer, Marker, Popup } from '@classic-homes/maps-svelte';\n\n<Map\n  title=\"My Map\"\n  center={[-122.4, 37.8]}\n  zoom={10}\n  height={400}\n  navigationControl\n>\n  <Source id=\"points\" data={geojson}>\n    <CircleLayer id=\"points-layer\" source=\"points\" circleRadius={6} circleColor=\"#3ba4a7\" />\n  </Source>\n  <Marker coordinates={[-122.4, 37.8]} color=\"red\" />\n</Map>",
    "peerDependency": "maplibre-gl@^4.0.0 || ^5.0.0",
    "optionalPeerDependencies": "@deck.gl/core, @deck.gl/layers, @deck.gl/mapbox (for DeckOverlay)",
    "cssImportOrder": "/* In main CSS file - order matters */\n@import '@classic-homes/theme-styles';\n@import 'maplibre-gl/dist/maplibre-gl.css';\n@import '@classic-homes/maps-core/styles/controls.css';\n@import '@classic-homes/maps-core/styles/popup.css';\n@import '@classic-homes/maps-core/styles/basemap-switcher.css';\n@import '@classic-homes/maps-core/styles/layer-manager.css';\n\n@tailwind base;\n@tailwind components;\n@tailwind utilities;",
    "tailwindConfig": "module.exports = {\n  presets: [require('@classic-homes/theme-tailwind-preset')],\n  content: [\n    './src/**/*.{js,ts,jsx,tsx}',\n    './node_modules/@classic-homes/maps-react/dist/**/*.{js,mjs}',\n  ],\n};"
  },
  "bestPractices": {
    "description": "Recommended patterns for using the maps packages effectively",
    "accessibility": {
      "title": "Accessibility",
      "guidelines": [
        "Always provide a descriptive `title` prop for the Map component - this becomes the aria-label",
        "Use `description` prop for additional context for screen readers",
        "The map supports full keyboard navigation: Arrow keys to pan, +/- to zoom, Shift+Arrow to rotate",
        "Use `generateMapDescription()` utility for dynamic accessible descriptions",
        "Markers and layers support `ariaLabel` for individual feature descriptions",
        "Consider providing a text alternative or data table for complex visualizations"
      ]
    },
    "performance": {
      "title": "Performance",
      "guidelines": [
        "Use clustering for large point datasets (>1000 points) via Source's `cluster` prop",
        "Set `generateId={true}` on Source when using feature state for hover/selection",
        "Use `minZoom` and `maxZoom` on layers to reduce rendering at unnecessary zoom levels",
        "Prefer GeoJSON data URLs over inline data for large datasets (enables caching)",
        "Use `loadGeoJSON()` utility for type-safe data loading with timeout handling",
        "Memoize GeoJSON data in React to prevent unnecessary re-renders",
        "For 100k+ features, use `buildSpatialIndex()` or `buildRTreeIndex()` for fast viewport culling",
        "Use `createGeoJSONFingerprint()` for O(1) equality checks instead of deep comparison",
        "Use `getChangedPaintProps()` to only update changed paint properties (minimizes MapLibre API calls)",
        "Consider `createIdleViewportManager()` for smooth panning with deferred updates",
        "Use `createPredictiveViewportManager()` to eliminate feature pop-in during panning",
        "For very large polygons, use LOD utilities with `simplifyPolygon()` for zoom-based simplification",
        "Use `SpatialWorkerClient` to move spatial indexing off the main thread for 100k+ features",
        "Set `hoverThrottleMs` on layers to throttle hover callbacks (feature-state updates remain immediate)"
      ]
    },
    "theming": {
      "title": "Theming",
      "guidelines": [
        "Use `theme='auto'` to automatically respect system dark/light preference",
        "All map UI elements (controls, popups, legends) inherit from design tokens",
        "Use `useMapTheme` hook to access resolved theme and style in custom components",
        "The `useReducedMotion` hook respects user's motion preferences for animations",
        "Custom layers can use theme colors via the `MapTheme` object from the hook"
      ]
    },
    "basemapSwitching": {
      "title": "Basemap Switching",
      "guidelines": [
        "Use the BasemapSwitcher control for built-in UI, or call setMapStyle from context for custom UI",
        "FREE_BASEMAPS provides 3 basemaps that work without API keys (OSM, Classic Light/Dark)",
        "Use apiKeys prop to enable MapTiler and Stadia Maps basemaps: apiKeys={{ maptiler: 'key', stadia: 'key' }}",
        "Use extraBasemaps to add custom basemap definitions alongside built-in ones",
        "Sources and layers are automatically re-initialized after basemap switch via the styleVersion mechanism",
        "Import basemap-switcher.css: @import '@classic-homes/maps-core/styles/basemap-switcher.css';"
      ]
    },
    "overlayLayers": {
      "title": "Overlay Layers (LayerManager)",
      "guidelines": [
        "Use LayerManager component for GIS overlay layers (school districts, parks, flood zones, etc.) on top of basemaps",
        "Configure built-in presets (PARKS, FLOOD_ZONES, SCHOOL_DISTRICTS, etc.) with your data URLs",
        "Use `showSearch` prop to enable layer filtering by name, description, or tags",
        "Use `showCategories` prop to group layers by category (boundaries, recreation, transportation, environmental)",
        "Use `showOpacity` prop to allow per-layer opacity adjustment",
        "Use `allowReorder` prop to enable drag-to-reorder layers",
        "Layers are automatically theme-aware - light/dark styles are applied based on document.documentElement.classList",
        "LayerManager handles basemap switching gracefully via styleVersion - layers are re-added after style change",
        "Create custom layers using OverlayLayerDefinition interface with source, style (light/dark), and optional legend",
        "Use `onLayerChange` callback to persist user preferences for layer visibility and opacity",
        "Import layer-manager.css: @import '@classic-homes/maps-core/styles/layer-manager.css';"
      ]
    },
    "layerOrdering": {
      "title": "Layer Ordering",
      "guidelines": [
        "Layers render in the order they appear in JSX/template",
        "Use `beforeId` prop to insert layers at specific positions",
        "Common order: Fill layers -> Line layers -> Circle layers -> Symbol layers",
        "Place highlight/selection layers after base layers",
        "Use z-index style properties within layer types for fine control"
      ]
    },
    "eventHandling": {
      "title": "Event Handling",
      "guidelines": [
        "Use layer-level `onClick` and `onHover` for feature interactions (more efficient than map-level)",
        "Feature events include full feature properties and geometry",
        "Use `feature.id` for feature state operations (hover highlighting)",
        "Marker events include marker-specific data and coordinates",
        "Map events provide viewport info (center, zoom, bearing, pitch)"
      ]
    },
    "clustering": {
      "title": "Clustering",
      "guidelines": [
        "Enable via `<Source cluster={true}>` or with config: `cluster={{ radius: 50, maxZoom: 14 }}`",
        "Use `isClusterFeature()` type guard to check if a feature is a cluster",
        "Use `expandCluster()` to zoom into clusters on click",
        "Cluster features have `cluster_id`, `point_count`, and `point_count_abbreviated` properties",
        "Style clusters differently using data-driven expressions based on `point_count`"
      ]
    },
    "popups": {
      "title": "Popups",
      "guidelines": [
        "Use PopupCard with PopupHeader/PopupContent/PopupFooter for structured popups",
        "Set `closeOnClick={false}` if popup should persist until explicitly closed",
        "Use `autoPan={true}` (default) to ensure popup is visible when opened",
        "Popup content can be any React/Svelte component",
        "Consider `maxWidth` prop for content-heavy popups"
      ]
    },
    "markers": {
      "title": "Markers",
      "guidelines": [
        "DOM-based Marker components use maplibregl.Marker which renders in the DOM",
        "DOM markers are susceptible to CSS framework interference (e.g., Tailwind) that can cause visual sliding during pan/rotate",
        "For production use with many markers or when CSS conflicts occur, prefer SymbolLayer which renders on the GPU canvas",
        "SymbolLayer is immune to CSS interference and generally performs better at scale",
        "Use Marker component only for simple cases or when you need custom HTML content that can't be achieved with icons",
        "If using DOM markers, the component applies CSS protection but this may not fully prevent sliding in all CSS environments"
      ]
    },
    "dynamicData": {
      "title": "Dynamic Data Loading",
      "description": "Patterns for loading and updating map data dynamically (async fetching, user interactions, etc.)",
      "guidelines": [
        "For dynamic marker-like points, use Source + CircleLayer/SymbolLayer instead of individual Marker components",
        "When Source data prop changes, the component automatically calls source.setData() to update layers efficiently",
        "Convert your data array to GeoJSON FeatureCollection format using useMemo/derived to avoid unnecessary re-renders",
        "Layers automatically re-render when their source data changes - no need to recreate layers",
        "Use generateId prop on Source to enable feature state (hover effects, selection)",
        "For popups with SymbolLayer, use onClick handler to get feature coordinates and properties",
        "This approach is more performant than DOM markers: single GPU draw call vs O(n) DOM operations",
        "DOM Markers also support dynamic loading - they appear immediately when added after map initialization"
      ],
      "recommendedPattern": {
        "name": "Dynamic SymbolLayer with Popup",
        "description": "Recommended pattern for dynamic points with popups - GPU-based, efficient, and immune to CSS conflicts",
        "react": "const [points, setPoints] = useState([]);\n\nconst geojson = useMemo(() => ({\n  type: 'FeatureCollection',\n  features: points.map(p => ({\n    type: 'Feature',\n    properties: { name: p.name, id: p.id },\n    geometry: { type: 'Point', coordinates: p.coordinates }\n  }))\n}), [points]);\n\nconst [selected, setSelected] = useState(null);\n\nreturn (\n  <Map title=\"Dynamic Points\">\n    <Source id=\"points\" type=\"geojson\" data={geojson} generateId>\n      <CircleLayer\n        id=\"circles\"\n        source=\"points\"\n        circleRadius={10}\n        circleColor=\"#3ba4a7\"\n        onClick={(e) => setSelected({ ...e.feature.properties, coordinates: e.lngLat })}\n        onHover={() => {}}\n      />\n      <SymbolLayer\n        id=\"labels\"\n        source=\"points\"\n        textField={['get', 'name']}\n        textOffset={[0, 1.5]}\n      />\n    </Source>\n    {selected && (\n      <Popup coordinates={selected.coordinates} onClose={() => setSelected(null)} closeButton>\n        <PopupCard>\n          <PopupHeader title={selected.name} />\n        </PopupCard>\n      </Popup>\n    )}\n  </Map>\n);",
        "svelte": "<script>\n  let points = $state([]);\n  let selected = $state(null);\n\n  const geojson = $derived({\n    type: 'FeatureCollection',\n    features: points.map(p => ({\n      type: 'Feature',\n      properties: { name: p.name, id: p.id },\n      geometry: { type: 'Point', coordinates: p.coordinates }\n    }))\n  });\n</script>\n\n<Map title=\"Dynamic Points\">\n  <Source id=\"points\" type=\"geojson\" data={geojson} generateId>\n    <CircleLayer\n      id=\"circles\"\n      source=\"points\"\n      circleRadius={10}\n      circleColor=\"#3ba4a7\"\n      onClick={(e) => selected = { ...e.feature.properties, coordinates: e.lngLat }}\n      onHover={() => {}}\n    />\n    <SymbolLayer id=\"labels\" source=\"points\" textField={['get', 'name']} textOffset={[0, 1.5]} />\n  </Source>\n  {#if selected}\n    <Popup coordinates={selected.coordinates} onClose={() => selected = null} closeButton>\n      <PopupCard><PopupHeader title={selected.name} /></PopupCard>\n    </Popup>\n  {/if}\n</Map>"
      },
      "comparisonTable": {
        "description": "Comparison of dynamic data approaches",
        "approaches": [
          {
            "name": "Source + SymbolLayer (Recommended)",
            "updateMechanism": "source.setData() on existing source",
            "performance": "Single GPU draw call",
            "cssImmunity": "Yes - rendered on canvas",
            "useCase": "Most marker-like use cases, especially with many points"
          },
          {
            "name": "DOM Markers",
            "updateMechanism": "Create/destroy marker instances",
            "performance": "O(n) DOM operations",
            "cssImmunity": "No - CSS protection applied but not guaranteed",
            "useCase": "Custom HTML content that can't be achieved with icons"
          }
        ]
      }
    },
    "mapboxMigration": {
      "title": "Migrating from Mapbox GL",
      "differences": [
        "No access token required (MapLibre is open source)",
        "Use `theme='light'` or `theme='dark'` instead of Mapbox style URLs",
        "Replace `mapboxgl.accessToken = 'pk...'` with nothing",
        "Replace `mapbox://styles/*` with theme prop or custom style URL",
        "Replace imperative `map.addLayer()` with declarative `<CircleLayer />` components",
        "Replace `map.on('click', layer, fn)` with `<Layer onClick={fn} />`"
      ],
      "exampleMigration": "// Before (Mapbox)\nimport mapboxgl from 'mapbox-gl';\nmapboxgl.accessToken = 'pk.xxx';\nconst map = new mapboxgl.Map({ container, style: 'mapbox://styles/...' });\nmap.on('load', () => {\n  map.addSource('data', { type: 'geojson', data });\n  map.addLayer({ id: 'points', type: 'circle', source: 'data' });\n});\n\n// After (Classic Theme)\nimport { Map, Source, CircleLayer } from '@classic-homes/maps-react';\n\n<Map title=\"Properties\" center={[-122.4, 37.8]} zoom={12}>\n  <Source id=\"data\" type=\"geojson\" data={geojson}>\n    <CircleLayer id=\"points\" source=\"data\" circleRadius={8} onClick={handleClick} />\n  </Source>\n</Map>"
    }
  },
  "themeTypes": {
    "description": "Theme-related TypeScript types",
    "types": [
      {
        "name": "MapTheme",
        "description": "Complete theme configuration for maps",
        "properties": [
          {
            "name": "name",
            "type": "string",
            "required": true,
            "description": "Theme identifier ('classic-light' or 'classic-dark')"
          },
          {
            "name": "colors",
            "type": "MapThemeColors",
            "required": true,
            "description": "UI and base colors"
          },
          {
            "name": "dataColors",
            "type": "MapDataColors",
            "required": true,
            "description": "Data visualization colors"
          },
          {
            "name": "typography",
            "type": "{ fontFamily: string; sizes: Record<string, number> }",
            "required": true,
            "description": "Typography settings"
          }
        ]
      },
      {
        "name": "MapThemeColors",
        "description": "Theme colors for map UI elements",
        "properties": [
          {
            "name": "background",
            "type": "string",
            "required": true,
            "description": "Map background color"
          },
          {
            "name": "text",
            "type": "string",
            "required": true,
            "description": "Primary text color"
          },
          {
            "name": "textMuted",
            "type": "string",
            "required": true,
            "description": "Muted text color"
          },
          {
            "name": "border",
            "type": "string",
            "required": true,
            "description": "Border color"
          },
          {
            "name": "water",
            "type": "string",
            "required": true,
            "description": "Water feature color"
          },
          {
            "name": "land",
            "type": "string",
            "required": true,
            "description": "Land/terrain color"
          },
          {
            "name": "road",
            "type": "string",
            "required": true,
            "description": "Road color"
          },
          {
            "name": "building",
            "type": "string",
            "required": true,
            "description": "Building color"
          },
          {
            "name": "park",
            "type": "string",
            "required": true,
            "description": "Park/green space color"
          }
        ]
      },
      {
        "name": "MapDataColors",
        "description": "Colors for data visualization layers (5 colors for series)",
        "properties": [
          {
            "name": "primary",
            "type": "string",
            "required": true,
            "description": "Primary data color (chart-1)"
          },
          {
            "name": "secondary",
            "type": "string",
            "required": true,
            "description": "Secondary data color (chart-2)"
          },
          {
            "name": "tertiary",
            "type": "string",
            "required": true,
            "description": "Tertiary data color (chart-3)"
          },
          {
            "name": "quaternary",
            "type": "string",
            "required": true,
            "description": "Quaternary data color (chart-4)"
          },
          {
            "name": "quinary",
            "type": "string",
            "required": true,
            "description": "Quinary data color (chart-5)"
          },
          {
            "name": "highlight",
            "type": "string",
            "required": true,
            "description": "Highlight/hover color"
          },
          {
            "name": "selection",
            "type": "string",
            "required": true,
            "description": "Selection color"
          }
        ]
      },
      {
        "name": "MapStyleSpec",
        "description": "MapLibre style specification (StyleSpecification from maplibre-gl)"
      },
      {
        "name": "ColorExpression",
        "description": "MapLibre expression that evaluates to a color (string | Expression)"
      },
      {
        "name": "NumberExpression",
        "description": "MapLibre expression that evaluates to a number (number | Expression)"
      }
    ]
  }
}